import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGameStore } from '../store/gameStore';
import { useAuthStore } from '../store/authStore';
import { getGame, updateGame, getTeam, addPlayer, fetchGames } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import AbbreviationKey from '../components/shared/AbbreviationKey';
import QuickTipsButton, { GAME_MODE_TIPS, GAME_MODE_TOUR } from '../components/shared/QuickTipsButton';
import { getPitchLimit } from '../components/shared/PitchCountBadge';
import GoLiveButton from '../components/shared/GoLiveButton';
import LightningDelayButton from '../components/shared/LightningDelayButton';
import { buildRestMap, getRestWarning, isRestEnforced } from '../lib/pitcherRest';
import { useHighlightTarget } from '../lib/highlightTarget';
import {
  RotateCcw, Zap, ChevronUp, ChevronDown, Shield, Swords,
  Users, Play, Target, AlertTriangle, Crosshair, UserPlus, ArrowRight,
  Repeat, X, Check, Share2, ArrowLeftRight, BookOpen
} from 'lucide-react';
import BenchModeDialog from '../components/shared/BenchModeDialog';

const API = process.env.REACT_APP_BACKEND_URL;
const POS_NUM = { P:1, C:2, '1B':3, '2B':4, '3B':5, SS:6, LF:7, CF:8, RF:9 };
const getPN = (p) => POS_NUM[p] || p;
const fmtSeq = (seq) => seq.map(p => getPN(p)).join('-');

// ======== BROADCAST DIAMOND (center stage, tappable) ========
const BroadcastDiamond = ({ runners, batterName, batterOrderNum, pitcherName, pitchCount, pitchLimit, isMyTeamPitching, onBaseTap, onPitcherTap, onPitchAdjust }) => {
  const r = runners || {};
  const cond = { fontFamily: 'Barlow Condensed, sans-serif' };
  const short = (n) => { const s = n || '?'; return s.length > 13 ? s.slice(0, 12) + '…' : s; };
  const pct = pitchLimit ? (pitchCount || 0) / pitchLimit : 0;
  const alertLevel = pitchLimit ? ((pitchCount || 0) >= pitchLimit ? 3 : pct >= 0.85 ? 2 : pct >= 0.6 ? 1 : 0) : 0;
  const pillBorder = alertLevel >= 2 ? 'rgba(239,68,68,0.9)' : alertLevel === 1 ? 'rgba(245,158,11,0.85)' : (isMyTeamPitching ? 'rgba(96,165,250,0.55)' : 'rgba(245,196,85,0.55)');
  const pillColor = alertLevel >= 2 ? '#F87171' : alertLevel === 1 ? '#FBBF24' : (isMyTeamPitching ? '#93C5FD' : '#F5C455');
  const pillText = pitcherName
    ? `${alertLevel === 3 ? '⚠ ' : ''}${short(pitcherName).toUpperCase()} · ${pitchCount || 0}${pitchLimit ? `/${pitchLimit}` : ''}P${alertLevel === 3 ? ' LIMIT' : ''}`
    : '+ SET PITCHER';
  const spots = [
    { base: 'first', cx: 300, cy: 195, left: '75%', top: '58%', tagTop: '46%' },
    { base: 'second', cx: 200, cy: 95, left: '50%', top: '28.3%', tagTop: '17%' },
    { base: 'third', cx: 100, cy: 195, left: '25%', top: '58%', tagTop: '46%' },
  ];
  return (
    <div className="relative w-full max-w-[470px] mx-auto select-none" style={{ aspectRatio: '400 / 336' }} data-testid="gm-broadcast-diamond">
      <svg viewBox="0 0 400 336" className="absolute inset-0 w-full h-full">
        <defs>
          <radialGradient id="bdGrass" cx="50%" cy="60%" r="75%">
            <stop offset="0%" stopColor="#123524" />
            <stop offset="70%" stopColor="#0D2419" />
            <stop offset="100%" stopColor="#0A1322" />
          </radialGradient>
          <radialGradient id="bdDirt" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#4A3620" />
            <stop offset="100%" stopColor="#2E2214" />
          </radialGradient>
          <filter id="bdGlow"><feGaussianBlur stdDeviation="4" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
        </defs>
        <path d="M 200 305 L 30 140 Q 200 -35 370 140 Z" fill="url(#bdGrass)" stroke="rgba(255,255,255,0.08)" strokeWidth="1.5" />
        <path d="M 200 297 L 98 195 Q 200 93 302 195 Z" fill="url(#bdDirt)" opacity="0.9" />
        <path d="M 200 290 L 300 195 L 200 95 L 100 195 Z" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="2.5" />
        <circle cx="200" cy="195" r="15" fill="#3A2B18" stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
        <rect x="194" y="193" width="12" height="3" fill="rgba(255,255,255,0.6)" />
        <rect x="193" y="283" width="14" height="14" fill="#fff" transform="rotate(45 200 290)" />
        <rect x="291" y="186" width="18" height="18" fill={r.first ? '#2EBE6B' : '#3A4A6F'} stroke={r.first ? '#2EBE6B' : 'rgba(255,255,255,0.5)'} strokeWidth="1.5" transform="rotate(45 300 195)" filter={r.first ? 'url(#bdGlow)' : undefined} />
        <rect x="191" y="86" width="18" height="18" fill={r.second ? '#2EBE6B' : '#3A4A6F'} stroke={r.second ? '#2EBE6B' : 'rgba(255,255,255,0.5)'} strokeWidth="1.5" transform="rotate(45 200 95)" filter={r.second ? 'url(#bdGlow)' : undefined} />
        <rect x="91" y="186" width="18" height="18" fill={r.third ? '#2EBE6B' : '#3A4A6F'} stroke={r.third ? '#2EBE6B' : 'rgba(255,255,255,0.5)'} strokeWidth="1.5" transform="rotate(45 100 195)" filter={r.third ? 'url(#bdGlow)' : undefined} />
      </svg>
      {spots.map(({ base, left, top, tagTop }) => (
        <React.Fragment key={base}>
          {r[base] && (
            <div className="absolute -translate-x-1/2 -translate-y-1/2 px-2.5 py-0.5 rounded-full border text-[12px] font-extrabold whitespace-nowrap pointer-events-none z-10"
              style={{ left, top: tagTop, background: 'rgba(10,19,34,0.92)', borderColor: '#2EBE6B', color: '#2EBE6B', ...cond }}
              data-testid={`gm-runner-tag-${base}`}>
              {short(r[base].playerName || r[base].name)}
            </div>
          )}
          <button type="button" onClick={() => onBaseTap(base)}
            className="absolute -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full active:bg-white/10"
            style={{ left, top }}
            data-testid={`gm-base-${base}`}
            aria-label={`${base} base`}
          />
        </React.Fragment>
      ))}
      <div className="absolute -translate-x-1/2 -translate-y-1/2 flex items-center gap-2 z-10" style={{ left: '50%', top: '68%' }}>
        <button type="button" onClick={() => onPitchAdjust(-1)} data-testid="gm-diamond-pitch-minus"
          className="w-9 h-9 rounded-full border border-white/25 text-slate-200 text-base font-bold active:bg-white/10"
          style={{ background: 'rgba(10,19,34,0.92)' }} aria-label="Subtract pitch">−</button>
        <button type="button" onClick={onPitcherTap} data-testid="gm-pitcher-display"
          className={cn("px-3 py-1.5 rounded-full border text-[12px] font-extrabold whitespace-nowrap", alertLevel === 3 && "animate-pulse")}
          style={{ background: 'rgba(10,19,34,0.92)', borderColor: pillBorder, color: pillColor, ...cond }}>
          {pillText}
        </button>
        <button type="button" onClick={() => onPitchAdjust(1)} data-testid="gm-diamond-pitch-plus"
          className="w-9 h-9 rounded-full border border-white/25 text-slate-200 text-base font-bold active:bg-white/10"
          style={{ background: 'rgba(10,19,34,0.92)' }} aria-label="Add pitch">+</button>
      </div>
      <div className="absolute -translate-x-1/2 px-4 py-1 rounded-full border-[1.5px] text-[13px] font-black whitespace-nowrap z-10"
        style={{ left: '50%', bottom: '1%', background: '#0E6B36', borderColor: '#2EBE6B', color: '#fff', ...cond }}
        data-testid="gm-atbat-tag">
        {`AT BAT: ${short(batterName).toUpperCase()} · #${batterOrderNum}`}
      </div>
    </div>
  );
};

const lastName = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  return parts.length > 1 ? parts[parts.length - 1] : parts[0];
};

export default function GameModePage() {
  const navigate = useNavigate();
  const { currentGameId, currentTeamId, liveScoringState, setLiveScoringState } = useGameStore();
  const { token } = useAuthStore();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  const playLogRef = useRef(null);

  // Core data
  const [game, setGame] = useState(null);
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Game State
  const [gs, setGs] = useState({
    battingOrder: [], currentBatterIndex: 0,
    balls: 0, strikes: 0, outs: 0,
    inning: 1, isTopInning: true,
    runners: { first: null, second: null, third: null },
    homeScore: 0, awayScore: 0,
    playHistory: []
  });

  // Opponent state
  const [oppLineup, setOppLineup] = useState([]);
  const [oppBatterIndex, setOppBatterIndex] = useState(0);
  const oppIdxRef = useRef(0);
  const setOppIdx = (v) => { oppIdxRef.current = v; setOppBatterIndex(v); };
  const [oppPitcherName, setOppPitcherName] = useState('');
  const [oppPitchCount, setOppPitchCount] = useState(0);

  // My pitcher
  const [myPitcher, setMyPitcher] = useState(null);
  const [myPitchCount, setMyPitchCount] = useState(0);

  // Logs
  const [playLog, setPlayLog] = useState([]);
  const [atBats, setAtBats] = useState([]);
  const [innings, setInnings] = useState([]);
  const [oppInnings, setOppInnings] = useState([]);

  // Dialogs
  const [hitDialog, setHitDialog] = useState(false);
  const [outDialog, setOutDialog] = useState(false);
  const [errorDialog, setErrorDialog] = useState(false);
  const [stealDialog, setStealDialog] = useState({ open: false, mode: 'steal', destinations: {}, caughtBase: null });
  const [wildPitchDialog, setWildPitchDialog] = useState(false);
  const [fcDialog, setFcDialog] = useState(false);
  const [runnerDialog, setRunnerDialog] = useState({ open: false, state: null, desc: '', placements: {}, batterBase: null, batterInfo: null, isSac: false, hitType: null });
  const [sprayDialog, setSprayDialog] = useState({ open: false, batterId: null, batterName: '', result: '', rbi: 0, inning: 1 });
  const [flyoutDialog, setFlyoutDialog] = useState({ open: false, state: null, desc: '', runners: {} });
  const [dropKDialog, setDropKDialog] = useState({ open: false, step: 'throw' });
  const [subDialog, setSubDialog] = useState({ open: false, type: '', playerIndex: null, playerId: null, selected: null });
  const [addRunnerDlg, setAddRunnerDlg] = useState({ open: false, base: null, selected: null });
  const [defenseMode, setDefenseMode] = useState(false);
  const [defenseSeq, setDefenseSeq] = useState([]);
  const [rareDialog, setRareDialog] = useState(false);
  const [catcherBalkDialog, setCatcherBalkDialog] = useState(false);
  const [changePitcherDialog, setChangePitcherDialog] = useState(false);
  const [quickSubOpen, setQuickSubOpen] = useState(false);
  const [quickSubTab, setQuickSubTab] = useState('auto'); // 'my' | 'opp' | 'auto'
  const [quickSubPick, setQuickSubPick] = useState({ open: false, side: null, slot: null, currentName: '', newName: '' });
  const [benchModeOpen, setBenchModeOpen] = useState(false);
  const [newPitcherName, setNewPitcherName] = useState('');
  const [addingNewPitcher, setAddingNewPitcher] = useState(false);
  const [restMap, setRestMap] = useState({});
  const [restWarnDialog, setRestWarnDialog] = useState({ open: false, player: null, warning: '' });
  const [runnerAdvanceDlg, setRunnerAdvanceDlg] = useState({ open: false, state: null, desc: '' });
  const [phDialog, setPhDialog] = useState({ open: false, playerIndex: null, playerId: null, newName: '' });
  const [phActive, setPhActive] = useState(null); // { position, originalId, originalName }
  const [redoStack, setRedoStack] = useState([]); // array of { gs, playLog, desc }
  const [undoToDialog, setUndoToDialog] = useState({ open: false, targetIndex: null, count: 0 });
  const [endGameDialog, setEndGameDialog] = useState(false);
  const [editOppPitcherDlg, setEditOppPitcherDlg] = useState({ open: false, name: '' });
  const [hrTypeDialog, setHrTypeDialog] = useState(false);
  const [softballRulesDialog, setSoftballRulesDialog] = useState(false);
  const [baseActionDlg, setBaseActionDlg] = useState({ open: false, base: null });
  const [rosterPromptDlg, setRosterPromptDlg] = useState(false);

  // Wire up the AbbreviationKey → button-finder. When a coach taps an abbreviation
  // on /key, we land here with a sessionStorage descriptor — open the matching
  // dialog if needed, then pulse the target button.
  useHighlightTarget('/game-mode', {
    gameInProgress: !!game,
    openDialog: (name) => {
      if (name === 'hits') setHitDialog(true);
      else if (name === 'outs') setOutDialog(true);
    },
  });

  // Close all open dialogs — used on Undo / Undo-to-Here
  const closeAllDialogs = () => {
    setHitDialog(false);
    setOutDialog(false);
    setErrorDialog(false);
    setWildPitchDialog(false);
    setFcDialog(false);
    setRareDialog(false);
    setStealDialog({ open: false, mode: 'steal', destinations: {}, caughtBase: null });
    setRunnerDialog({ open: false, state: null, desc: '', placements: {}, batterBase: null, batterInfo: null, isSac: false, hitType: null });
    setSprayDialog({ open: false, batterId: null, batterName: '', result: '', rbi: 0, inning: 1 });
    setFlyoutDialog({ open: false, state: null, desc: '', runners: {} });
    setDropKDialog({ open: false, step: 'throw' });
    setRunnerAdvanceDlg({ open: false, state: null, desc: '' });
    setHrTypeDialog(false);
    setBaseActionDlg({ open: false, base: null });
    setRosterPromptDlg(false);
  };

  // Derived
  const isHome = game?.is_home !== false;
  const myTeamBatting = isHome ? !gs.isTopInning : gs.isTopInning;
  const currentBatter = myTeamBatting
    ? team?.players?.find(p => p.id === gs.battingOrder[gs.currentBatterIndex])
    : null;
  const currentOppBatter = !myTeamBatting ? oppLineup[oppBatterIndex] : null;
  const batterName = myTeamBatting ? lastName(currentBatter?.name) : lastName(currentOppBatter?.name);
  const teamName = team?.name || 'My Team';
  const oppName = game?.opponent_name || 'Opponent';
  const myScore = isHome ? gs.homeScore : gs.awayScore;
  const oppScore = isHome ? gs.awayScore : gs.homeScore;
  const benchPlayers = team?.players?.filter(p => !gs.battingOrder.includes(p.id)) || [];
  const myPitchLimit = getPitchLimit(team?.age_group, team?.custom_pitch_limit);

  // Pitch-count safety alerts (toast once per threshold per pitcher)
  const pitchAlertRef = useRef({ pitcherId: null, level: 0 });
  useEffect(() => {
    if (myTeamBatting || !myPitcher) return;
    const pct = myPitchCount / myPitchLimit;
    const level = myPitchCount >= myPitchLimit ? 3 : pct >= 0.85 ? 2 : pct >= 0.6 ? 1 : 0;
    const ref = pitchAlertRef.current;
    if (ref.pitcherId !== myPitcher.id) { ref.pitcherId = myPitcher.id; ref.level = 0; }
    if (level > ref.level) {
      ref.level = level;
      if (level === 3) toast.error(`${myPitcher.name} has reached the ${myPitchLimit}-pitch limit — a pull is required`, { duration: 8000 });
      else if (level === 2) toast.warning(`${myPitcher.name} at ${myPitchCount}/${myPitchLimit} pitches — almost at the limit`, { duration: 6000 });
      else if (level === 1) toast(`${myPitcher.name} at ${myPitchCount}/${myPitchLimit} pitches — consider warming up a reliever`, { duration: 5000 });
    }
  }, [myPitchCount, myPitcher, myTeamBatting, myPitchLimit]);

  useEffect(() => {
    if (!currentGameId || !currentTeamId) { navigate('/dashboard'); return; }
    const timer = setTimeout(loadData, 300); // Small delay to let previous page saves complete
    return () => clearTimeout(timer);
  }, [currentGameId, currentTeamId]);

  // Re-fetch when returning from another browser tab (not on every focus - that races with saves)
  const lastSaveRef = useRef(0);
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && currentGameId && Date.now() - lastSaveRef.current > 2000) loadData();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [currentGameId, currentTeamId]);

  useEffect(() => { if (playLogRef.current) playLogRef.current.scrollTop = 0; }, [playLog.length]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [gameData, teamData] = await Promise.all([getGame(currentGameId), getTeam(currentTeamId)]);
      setGame(gameData);
      setTeam(teamData);
      // Fetch recent games for pitcher rest-day warnings (non-blocking on failure)
      // Skip entirely when team opts out of rest rules (e.g., softball / travel baseball).
      if (isRestEnforced(teamData)) {
        fetchGames(currentTeamId, gameData.season).then(games => {
          setRestMap(buildRestMap(games || [], currentGameId));
        }).catch(() => setRestMap({}));
      } else {
        setRestMap({});
      }
      // Resolve runner IDs to full objects with names
      const rawRunners = gameData.runners_on_base || { first: null, second: null, third: null };
      const resolveRunner = (id) => {
        if (!id) return null;
        if (typeof id === 'object') return id;
        // Try our team by player ID
        const p = teamData.players?.find(pl => pl.id === id);
        if (p) return { playerId: p.id, playerName: lastName(p.name) };
        // Try opponent lineup by name
        const opp = (gameData.opponent_lineup || []).find(o => o.name === id || o.name?.split(' ').pop() === id);
        if (opp) return { playerId: null, playerName: lastName(opp.name) };
        // Fallback - treat as a name string
        return { playerId: null, playerName: lastName(id) };
      };
      
      // Check if liveScoringState from another page is fresher
      const ls = liveScoringState;
      const useLive = ls && ls.gameId === currentGameId && (Date.now() - ls.timestamp < 5000);
      
      setGs({
        battingOrder: gameData.batting_order || [],
        currentBatterIndex: useLive ? ls.currentBatterIndex : (gameData.current_batter_index ?? 0),
        balls: useLive ? ls.balls : (gameData.current_balls || 0),
        strikes: useLive ? ls.strikes : (gameData.current_strikes || 0),
        outs: useLive ? ls.outs : (gameData.current_outs || 0),
        inning: useLive ? ls.inning : (gameData.current_inning || 1),
        isTopInning: useLive ? ls.isTopInning : (gameData.is_top_inning ?? true),
        runners: useLive && ls.runners ? ls.runners : { first: resolveRunner(rawRunners.first), second: resolveRunner(rawRunners.second), third: resolveRunner(rawRunners.third) },
        homeScore: useLive ? ls.homeScore : (gameData.home_score || 0),
        awayScore: useLive ? ls.awayScore : (gameData.away_score || 0),
        playHistory: []
      });
      if ((gameData.batting_order || []).length === 0 && (teamData.players || []).length > 0 && !sessionStorage.getItem(`gm_roster_prompt_${currentGameId}`)) {
        setRosterPromptDlg(true);
      }
      setOppLineup(gameData.opponent_lineup || []);
      setOppIdx(useLive ? (ls.oppBatterIndex ?? gameData.opponent_current_batter_index ?? 0) : (gameData.opponent_current_batter_index || 0));
      setOppPitcherName(gameData.opponent_pitcher_name || '');
      setOppPitchCount(gameData.opponent_pitch_count || 0);
      // Use play_log from store if fresher, otherwise DB
      setPlayLog(useLive && ls.playLog ? ls.playLog : (gameData.play_log || []));
      setAtBats(gameData.at_bats || []);
      setInnings(gameData.innings || []);
      setOppInnings(gameData.opponent_innings || []);
      
      // Try to find pitcher - first by current_pitcher_id, then from field_positions
      let pitcherFound = false;
      if (gameData.current_pitcher_id) {
        const pitcher = teamData.players?.find(p => p.id === gameData.current_pitcher_id);
        if (pitcher) {
          setMyPitcher(pitcher);
          setMyPitchCount(gameData.pitch_count || 0);
          pitcherFound = true;
        }
      }
      
      // If not found by current_pitcher_id, try field_positions
      if (!pitcherFound) {
        const fp = gameData.field_positions;
        if (fp?.length > 0) {
          const latest = fp[fp.length - 1];
          const pitcherPos = latest?.positions?.find(p => p.position === 'P');
          if (pitcherPos?.player_id) {
            const pitcher = teamData.players?.find(p => p.id === pitcherPos.player_id);
            if (pitcher) { 
              setMyPitcher(pitcher); 
              setMyPitchCount(gameData.pitch_count || 0); 
            } else if (pitcherPos.player_name) {
              // Fallback: use player_name from field_positions if player not found in team
              setMyPitcher({ id: pitcherPos.player_id, name: pitcherPos.player_name });
              setMyPitchCount(gameData.pitch_count || 0);
            }
          }
        }
      }
    } catch { toast.error('Failed to load game'); }
    finally { setLoading(false); }
  }, [currentGameId, currentTeamId]);

  // ===== SAVE =====
  const save = async (newGs, desc, wasThreeOuts = false) => {
    setSaving(true);
    lastSaveRef.current = Date.now();
    // Any forward action invalidates the redo stack
    if (redoStack.length > 0) setRedoStack([]);
    try {
      const rBE = {
        first: newGs.runners.first ? (newGs.runners.first.playerId || newGs.runners.first.playerName || null) : null,
        second: newGs.runners.second ? (newGs.runners.second.playerId || newGs.runners.second.playerName || null) : null,
        third: newGs.runners.third ? (newGs.runners.third.playerId || newGs.runners.third.playerName || null) : null
      };
      let newLog = playLog;
      if (desc) {
        const entry = { id: Date.now(), description: desc, inning: newGs.inning, isTopInning: newGs.isTopInning,
          outs: newGs.outs, score: `${newGs.awayScore}-${newGs.homeScore}`, time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) };
        newLog = [...playLog, entry];
        setPlayLog(newLog);
      }
      // Inning tracking
      const playInning = gs.inning;
      const hrd = newGs.homeScore - gs.homeScore;
      const ard = newGs.awayScore - gs.awayScore;
      const ourRuns = isHome ? hrd : ard;
      const theirRuns = isHome ? ard : hrd;
      let ui = [...innings], uoi = [...oppInnings];
      if (ourRuns) { const i = ui.findIndex(x=>x.inning===playInning); if(i>=0) ui[i]={...ui[i],runs:(ui[i].runs||0)+ourRuns}; else ui.push({inning:playInning,runs:ourRuns}); }
      if (theirRuns) { const i = uoi.findIndex(x=>x.inning===playInning); if(i>=0) uoi[i]={...uoi[i],runs:(uoi[i].runs||0)+theirRuns}; else uoi.push({inning:playInning,runs:theirRuns}); }
      setInnings(ui); setOppInnings(uoi);

      // Update local + Zustand store IMMEDIATELY (before API call)
      setGs(newGs);
      setLiveScoringState({
        gameId: currentGameId, currentBatterIndex: newGs.currentBatterIndex,
        balls: newGs.balls, strikes: newGs.strikes, outs: newGs.outs,
        inning: newGs.inning, isTopInning: newGs.isTopInning,
        runners: newGs.runners, homeScore: newGs.homeScore, awayScore: newGs.awayScore,
        oppBatterIndex: oppIdxRef.current, currentPitcherId: myPitcher?.id, pitchCount: myPitchCount,
        playLog: newLog
      });

      // Fire-and-forget API save (don't block UI)
      updateGame(currentGameId, {
        current_inning: newGs.inning, current_outs: newGs.outs, runners_on_base: rBE,
        home_score: newGs.homeScore, away_score: newGs.awayScore,
        current_batter_index: newGs.currentBatterIndex, is_top_inning: newGs.isTopInning,
        current_balls: newGs.balls, current_strikes: newGs.strikes,
        pitch_count: myPitchCount, opponent_pitch_count: oppPitchCount,
        play_log: newLog, innings: ui, opponent_innings: uoi,
        opponent_current_batter_index: oppIdxRef.current,
        current_pitcher_id: myPitcher?.id,
        batting_order: newGs.battingOrder
      }).catch(() => toast.error('Failed to save to server'));
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  // Persist a restored snapshot (undo/redo/rewind) so the server matches the UI
  const persistSnapshot = (s, log, oppIdx) => {
    const rBE = {
      first: s.runners.first ? (s.runners.first.playerId || s.runners.first.playerName || null) : null,
      second: s.runners.second ? (s.runners.second.playerId || s.runners.second.playerName || null) : null,
      third: s.runners.third ? (s.runners.third.playerId || s.runners.third.playerName || null) : null
    };
    updateGame(currentGameId, {
      current_inning: s.inning, current_outs: s.outs, runners_on_base: rBE,
      home_score: s.homeScore, away_score: s.awayScore,
      current_batter_index: s.currentBatterIndex, is_top_inning: s.isTopInning,
      current_balls: s.balls, current_strikes: s.strikes,
      play_log: log, opponent_current_batter_index: oppIdx
    }).catch(() => {});
  };

  // ===== HELPERS =====
  const incOppPitch = () => { const c = oppPitchCount + 1; setOppPitchCount(c); updateGame(currentGameId, { opponent_pitch_count: c }).catch(()=>{}); };
  const incMyPitch = () => { const c = myPitchCount + 1; setMyPitchCount(c); updateGame(currentGameId, { pitch_count: c }).catch(()=>{}); };
  const advanceBatter = () => {
    if (myTeamBatting) return (gs.currentBatterIndex + 1) % (gs.battingOrder.length || 1);
    else { setOppIdx((oppIdxRef.current + 1) % (oppLineup.length || 1)); return gs.currentBatterIndex; }
  };
  const pushHistory = () => ({
    ...gs,
    runners: { ...gs.runners },
    playHistory: [...gs.playHistory, { ...gs, runners: { ...gs.runners }, _oppIdx: oppIdxRef.current }]
  });
  const addScore = (s, runs) => {
    if (myTeamBatting) { if (isHome) s.homeScore += runs; else s.awayScore += runs; }
    else { if (isHome) s.awayScore += runs; else s.homeScore += runs; }
  };
  const checkThreeOuts = (s, desc, batterPaComplete = true) => {
    if (s.outs >= 3) {
      s.outs = 0; s.runners = { first: null, second: null, third: null };
      s.balls = 0; s.strikes = 0;
      if (batterPaComplete) {
        if (myTeamBatting) s.currentBatterIndex = (gs.currentBatterIndex + 1) % (gs.battingOrder.length || 1);
        else setOppIdx((oppIdxRef.current + 1) % (oppLineup.length || 1));
      }
      if (s.isTopInning) { s.isTopInning = false; } else { s.isTopInning = true; s.inning += 1; }
      save(s, `${desc} - 3 Outs!`, true);
      const halfLabel = s.isTopInning ? `Top of ${s.inning}` : `Bottom of ${s.inning}`;
      toast.success(`🔁 Side changed — ${halfLabel}`, {
        description: 'Tap Undo to revert this side-switch',
        duration: 5000,
        action: { label: 'Undo', onClick: () => handleUndo() },
      });
      return true;
    }
    return false;
  };

  // ===== BALL / STRIKE =====
  const handleBall = () => {
    if (myTeamBatting) incOppPitch(); else incMyPitch();
    const s = pushHistory();
    s.balls += 1;
    if (s.balls >= 4) { handleWalk(s); return; }
    save(s, `Ball - ${batterName} (${s.balls}-${s.strikes})`);
  };
  const handleStrike = () => {
    if (myTeamBatting) incOppPitch(); else incMyPitch();
    const s = pushHistory();
    s.strikes += 1;
    if (s.strikes >= 3) { handleOut(s, `Strikeout (K) - ${batterName}`); return; }
    save(s, `Strike - ${batterName} (${s.balls}-${s.strikes})`);
  };
  const handleFoul = () => {
    if (myTeamBatting) incOppPitch(); else incMyPitch();
    save({ ...gs, playHistory: [...gs.playHistory, { ...gs, _oppIdx: oppIdxRef.current }] }, `Foul Ball - ${batterName} (${gs.balls}-${gs.strikes})`);
  };
  const handleChangeSide = () => {
    const s = pushHistory();
    s.outs = 0; s.runners = { first: null, second: null, third: null };
    s.balls = 0; s.strikes = 0;
    s.isTopInning = !s.isTopInning;
    save(s, `Side changed manually → ${s.isTopInning ? 'TOP' : 'BOT'} ${s.inning}`);
    toast.info(s.isTopInning ? `Top of ${s.inning}` : `Bottom of ${s.inning}`);
  };

  // ===== RARE PLAYS =====
  const handleGroundRuleDouble = () => {
    if (myTeamBatting) incOppPitch(); else incMyPitch();
    const s = pushHistory();
    const bi = myTeamBatting ? { playerId: currentBatter?.id, playerName: lastName(currentBatter?.name) } : { playerId: null, playerName: lastName(currentOppBatter?.name) };
    let runs = 0;
    if (s.runners.third) runs++;
    if (s.runners.second) runs++;
    if (s.runners.first) s.runners.third = s.runners.first;
    s.runners.second = bi; s.runners.first = null;
    addScore(s, runs); s.balls = 0; s.strikes = 0; s.currentBatterIndex = advanceBatter();
    save(s, `Ground Rule Double - ${batterName}${runs ? ` (${runs} R)` : ''}`);
    setRareDialog(false);
    if (myTeamBatting && currentBatter) setSprayDialog({ open: true, batterId: currentBatter.id, batterName: currentBatter.name, result: 'double', rbi: runs, inning: s.inning });
  };
  const handleTriplePlay = () => {
    const s = pushHistory();
    s.outs += 3; s.runners = { first: null, second: null, third: null }; s.balls = 0; s.strikes = 0;
    checkThreeOuts(s, `TRIPLE PLAY! - ${batterName}`);
    setRareDialog(false);
  };
  const handleIntentionalWalk = () => {
    const s = pushHistory();
    const bi = myTeamBatting ? { playerId: currentBatter?.id, playerName: lastName(currentBatter?.name) } : { playerId: null, playerName: lastName(currentOppBatter?.name) };
    let runs = 0;
    if (s.runners.first) { if (s.runners.second) { if (s.runners.third) runs++; s.runners.third = s.runners.second; } s.runners.second = s.runners.first; }
    s.runners.first = bi; addScore(s, runs); s.balls = 0; s.strikes = 0; s.currentBatterIndex = advanceBatter();
    save(s, `Intentional Walk (IBB) - ${batterName}${runs ? ` (${runs} R)` : ''}`);
    setRareDialog(false);
  };
  const handleInfieldFly = () => {
    const s = pushHistory();
    s.outs += 1; s.balls = 0; s.strikes = 0;
    if (!checkThreeOuts(s, `Infield Fly Rule - ${batterName} out`)) {
      s.currentBatterIndex = advanceBatter();
      save(s, `Infield Fly Rule - ${batterName} out`);
    }
    setRareDialog(false);
  };
  const handleInterference = (type) => {
    const s = pushHistory();
    const bi = myTeamBatting ? { playerId: currentBatter?.id, playerName: lastName(currentBatter?.name) } : { playerId: null, playerName: lastName(currentOppBatter?.name) };
    s.runners.first = bi; s.balls = 0; s.strikes = 0; s.currentBatterIndex = advanceBatter();
    save(s, `${type} Interference - ${batterName} awarded 1st`);
    setRareDialog(false);
  };
  // === Catcher Balk: Squeeze / Steal of Home ===
  // Catcher's interference during a steal-of-home attempt. Runner on 3B scores
  // automatically (would've scored anyway), batter is awarded 1B, and any forced
  // baserunners advance (R1 forced to 2B, R2 forced to 3B when R1 also present).
  // Charged as a balk against the pitcher, even though the violation is on the catcher.
  const handleCatcherBalkSqueeze = () => {
    const s = pushHistory();
    const bi = myTeamBatting ? { playerId: currentBatter?.id, playerName: lastName(currentBatter?.name) } : { playerId: null, playerName: lastName(currentOppBatter?.name) };
    let runs = 0;
    // R3 scores automatically
    if (s.runners.third) { runs += 1; s.runners.third = null; }
    // Force chain because batter takes 1B
    if (s.runners.first) {
      if (s.runners.second) { s.runners.third = s.runners.second; }
      s.runners.second = s.runners.first;
    }
    s.runners.first = bi;
    addScore(s, runs);
    s.balls = 0; s.strikes = 0; s.currentBatterIndex = advanceBatter();
    save(s, `Catcher Balk (Squeeze) — ${batterName} awarded 1st${runs ? ` · R3 scores (${runs} R)` : ''}`);
    setCatcherBalkDialog(false);
    setRareDialog(false);
  };
  // === Catcher Balk: Detached Equipment ===
  // Catcher used detached equipment (mask, helmet, etc.) to touch a pitched ball.
  // Penalty: all active baserunners advance one base. Batter stays at the plate
  // with the count reset (the pitch is voided).
  const handleCatcherBalkDetached = () => {
    const s = pushHistory();
    let runs = 0;
    if (s.runners.third) { runs += 1; s.runners.third = null; }
    if (s.runners.second) { s.runners.third = s.runners.second; s.runners.second = null; }
    if (s.runners.first) { s.runners.second = s.runners.first; s.runners.first = null; }
    addScore(s, runs);
    s.balls = 0; s.strikes = 0;
    save(s, `Catcher Balk (Detached Equipment) — All runners +1 base${runs ? ` (${runs} R)` : ''}`);
    setCatcherBalkDialog(false);
    setRareDialog(false);
  };

  // ===== TOGGLE TOP/BOT =====
  const toggleHalf = () => {
    const s = { ...gs }; s.isTopInning = !s.isTopInning;
    setGs(s); save(s, `Half changed → ${s.isTopInning ? 'TOP' : 'BOT'} ${s.inning}`);
  };

  // ===== CHANGE BATTER =====
  const handleChangeBatter = (index) => {
    if (myTeamBatting) {
      const s = { ...gs, currentBatterIndex: index, balls: 0, strikes: 0 };
      setGs(s); save(s, `Batter changed to #${index + 1}`);
    } else {
      setOppIdx(index);
      updateGame(currentGameId, { opponent_current_batter_index: index }).catch(() => {});
    }
  };

  // ===== PINCH HITTER =====
  const applyPitcherChange = (p) => {
    setMyPitcher(p);
    setMyPitchCount(0);
    updateGame(currentGameId, { current_pitcher_id: p.id, pitch_count: 0 }).catch(() => {});
    const s = { ...gs, playHistory: [...gs.playHistory, { ...gs, _oppIdx: oppIdxRef.current }] };
    save(s, `Pitcher change: ${lastName(p.name)}`);
    setChangePitcherDialog(false);
    toast.success(`${p.name} now pitching`);
  };

  const handlePitcherPick = (p) => {
    const warning = getRestWarning(restMap, p.id);
    if (warning) {
      setRestWarnDialog({ open: true, player: p, warning });
      return;
    }
    applyPitcherChange(p);
  };

  const handleAddNewPitcher = async () => {
    const name = newPitcherName.trim();
    if (!name) return;
    try {
      setAddingNewPitcher(true);
      const newPlayer = await addPlayer(currentTeamId, { name, jersey_number: '', position: 'P', is_pitcher: true });
      const freshTeam = await getTeam(currentTeamId);
      setTeam(freshTeam);
      setMyPitcher(newPlayer);
      setMyPitchCount(0);
      updateGame(currentGameId, { current_pitcher_id: newPlayer.id, pitch_count: 0 }).catch(() => {});
      const s = { ...gs, playHistory: [...gs.playHistory, { ...gs, _oppIdx: oppIdxRef.current }] };
      save(s, `Pitcher change: ${lastName(name)}`);
      setNewPitcherName('');
      setChangePitcherDialog(false);
      toast.success(`${name} added & now pitching`);
    } catch {
      toast.error('Failed to add pitcher');
    } finally {
      setAddingNewPitcher(false);
    }
  };

  const handlePhConfirm = async () => {
    const { playerIndex, newName } = phDialog;
    if (!newName.trim() || playerIndex === null) return;
    const oldPlayer = team?.players?.find(p => p.id === gs.battingOrder[playerIndex]);
    // Add new player to team
    try {
      const newPlayer = await addPlayer(currentTeamId, { name: newName.trim(), jersey_number: '', position: '' });
      const newOrder = [...gs.battingOrder]; newOrder[playerIndex] = newPlayer.id;
      const s = { ...gs, battingOrder: newOrder, playHistory: [...gs.playHistory, { ...gs, _oppIdx: oppIdxRef.current }] };
      setPhActive({ position: playerIndex, originalId: oldPlayer?.id, originalName: oldPlayer?.name, phId: newPlayer.id });
      save(s, `Pinch Hitter: ${newName.trim()} for ${oldPlayer?.name || '?'} at #${playerIndex + 1}`);
      const freshTeam = await getTeam(currentTeamId); setTeam(freshTeam);
      toast.success(`${newName.trim()} in for ${oldPlayer?.name}`);
    } catch { toast.error('Failed to add pinch hitter'); }
    setPhDialog({ open: false, playerIndex: null, playerId: null, newName: '' });
  };
  const handlePhSwitchBack = (yes) => {
    if (yes && phActive) {
      const newOrder = [...gs.battingOrder]; newOrder[phActive.position] = phActive.originalId;
      const s = { ...gs, battingOrder: newOrder, playHistory: [...gs.playHistory, { ...gs, _oppIdx: oppIdxRef.current }] };
      save(s, `${phActive.originalName} back in lineup at #${phActive.position + 1}`);
      toast.success(`${phActive.originalName} is back`);
    }
    setPhActive(null);
  };

  // ===== QUICK SUB (tap a batting slot on either team, swap or add new player) =====
  const openQuickSubPick = (side, slot, currentName) => {
    setQuickSubPick({ open: true, side, slot, currentName, newName: '' });
  };

  const quickSubApplyExisting = async (playerId) => {
    const { side, slot } = quickSubPick;
    if (side === 'my') {
      const oldPlayer = team?.players?.find(p => p.id === gs.battingOrder[slot]);
      const newPlayer = team?.players?.find(p => p.id === playerId);
      if (!newPlayer) return;
      const newOrder = [...gs.battingOrder]; newOrder[slot] = playerId;
      const s = { ...gs, battingOrder: newOrder, playHistory: [...gs.playHistory, { ...gs, _oppIdx: oppIdxRef.current }] };
      save(s, `Sub: ${newPlayer.name} in for ${oldPlayer?.name || '?'} at #${slot + 1}`);
      toast.success(`${newPlayer.name} in for ${oldPlayer?.name || '?'}`);
    }
    setQuickSubPick({ open: false, side: null, slot: null, currentName: '', newName: '' });
  };

  const quickSubAddNew = async () => {
    const { side, slot, newName } = quickSubPick;
    const name = (newName || '').trim();
    if (!name) return;
    if (side === 'my') {
      try {
        const newPlayer = await addPlayer(currentTeamId, { name, jersey_number: '', position: '' });
        const oldPlayer = team?.players?.find(p => p.id === gs.battingOrder[slot]);
        const newOrder = [...gs.battingOrder]; newOrder[slot] = newPlayer.id;
        const s = { ...gs, battingOrder: newOrder, playHistory: [...gs.playHistory, { ...gs, _oppIdx: oppIdxRef.current }] };
        save(s, `Sub: ${name} in for ${oldPlayer?.name || '?'} at #${slot + 1}`);
        const freshTeam = await getTeam(currentTeamId); setTeam(freshTeam);
        toast.success(`${name} added + subbed in`);
      } catch { toast.error('Failed to add player'); return; }
    } else {
      // Opponent: update oppLineup[slot].name
      const newLineup = [...oppLineup];
      const oldName = newLineup[slot]?.name || '';
      newLineup[slot] = { ...newLineup[slot], name };
      setOppLineup(newLineup);
      updateGame(currentGameId, { opponent_lineup: newLineup }).catch(() => {});
      save(gs, `Sub: ${name} in for ${oldName || '?'} at #${slot + 1} (${oppName})`);
      toast.success(`${name} in for ${oldName || '?'}`);
    }
    setQuickSubPick({ open: false, side: null, slot: null, currentName: '', newName: '' });
  };

  // Detect PH at-bat complete
  useEffect(() => {
    if (!phActive) return;
    if (gs.currentBatterIndex !== phActive.position) {
      // PH's at-bat is done — ask to switch back
      const timer = setTimeout(() => {
        if (window.confirm(`Switch ${phActive.originalName} back into the lineup?`)) handlePhSwitchBack(true);
        else handlePhSwitchBack(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [gs.currentBatterIndex, phActive]);

  // ===== WALK / HBP =====
  const handleWalk = (state) => {
    const s = state || pushHistory();
    const bi = myTeamBatting ? { playerId: currentBatter?.id, playerName: lastName(currentBatter?.name) } : { playerId: null, playerName: lastName(currentOppBatter?.name) };
    let runs = 0;
    if (s.runners.first) { if (s.runners.second) { if (s.runners.third) runs++; s.runners.third = s.runners.second; } s.runners.second = s.runners.first; }
    s.runners.first = bi;
    addScore(s, runs);
    s.balls = 0; s.strikes = 0;
    s.currentBatterIndex = advanceBatter();
    save(s, `Walk (BB) - ${batterName}${runs ? ` (${runs} R)` : ''}`);
    setHitDialog(false);
  };
  const handleHBP = () => {
    if (myTeamBatting) incOppPitch(); else incMyPitch();
    const s = pushHistory();
    const bi = myTeamBatting ? { playerId: currentBatter?.id, playerName: lastName(currentBatter?.name) } : { playerId: null, playerName: lastName(currentOppBatter?.name) };
    let runs = 0;
    if (s.runners.first) { if (s.runners.second) { if (s.runners.third) runs++; s.runners.third = s.runners.second; } s.runners.second = s.runners.first; }
    s.runners.first = bi;
    addScore(s, runs);
    s.balls = 0; s.strikes = 0; s.currentBatterIndex = advanceBatter();
    save(s, `HBP - ${batterName}${runs ? ` (${runs} R)` : ''}`);
    setHitDialog(false);
  };

  // ===== HIT =====
  const handleHit = (type) => {
    if (type === 'homerun') {
      // Open the In-the-Park / Over-the-Fence selector instead of scoring immediately
      setHitDialog(false);
      setHrTypeDialog(true);
      return;
    }
    if (myTeamBatting) incOppPitch(); else incMyPitch();
    const s = pushHistory();
    const bi = myTeamBatting ? { playerId: currentBatter?.id, playerName: lastName(currentBatter?.name) } : { playerId: null, playerName: lastName(currentOppBatter?.name) };
    s.balls = 0; s.strikes = 0;
    const hasRunners = s.runners.first || s.runners.second || s.runners.third;
    const baseMap = { single: 'first', double: 'second', triple: 'third' };
    const bb = baseMap[type];
    if (!hasRunners) {
      s.runners[bb] = bi;
      s.currentBatterIndex = advanceBatter();
      const hNames = { single: 'Single', double: 'Double', triple: 'Triple' };
      save(s, `${hNames[type]} - ${batterName}`);
      setHitDialog(false);
      if (myTeamBatting && currentBatter) setSprayDialog({ open: true, batterId: currentBatter.id, batterName: currentBatter.name, result: type, rbi: 0, inning: s.inning });
      return;
    }
    // Runners on base → runner placement dialog
    s.currentBatterIndex = advanceBatter();
    const smartP = {};
    if (type === 'single') { if (s.runners.third) smartP.third='home'; if (s.runners.second) smartP.second='third'; if (s.runners.first) smartP.first='second'; }
    else if (type === 'double') { if (s.runners.third) smartP.third='home'; if (s.runners.second) smartP.second='home'; if (s.runners.first) smartP.first='third'; }
    else if (type === 'triple') { if (s.runners.third) smartP.third='home'; if (s.runners.second) smartP.second='home'; if (s.runners.first) smartP.first='home'; }
    const hNames = { single: 'Single', double: 'Double', triple: 'Triple' };
    setRunnerDialog({ open: true, state: s, desc: `${hNames[type]} - ${batterName}`, placements: smartP, batterBase: bb, batterInfo: bi, isSac: false, hitType: type });
    setHitDialog(false);
  };

  // Home run with type selector - 'in_the_park' or 'over_the_fence'
  const handleHomeRun = (hrType) => {
    if (myTeamBatting) incOppPitch(); else incMyPitch();
    const s = pushHistory();
    s.balls = 0; s.strikes = 0;
    let runs = 1;
    if (s.runners.third) runs++; if (s.runners.second) runs++; if (s.runners.first) runs++;
    s.runners = { first: null, second: null, third: null };
    addScore(s, runs);
    s.currentBatterIndex = advanceBatter();
    const label = hrType === 'in_the_park' ? 'in the park' : 'over the fence';
    save(s, `HOME RUN! (${label}) - ${batterName} (${runs} R)`);
    setHrTypeDialog(false);
    if (myTeamBatting && currentBatter) {
      setSprayDialog({
        open: true,
        batterId: currentBatter.id,
        batterName: currentBatter.name,
        result: hrType === 'in_the_park' ? 'home_run_park' : 'home_run',
        rbi: runs,
        inning: s.inning,
      });
    }
  };

  // ===== ERROR =====
  const handleError = (bases) => {
    if (myTeamBatting) incOppPitch(); else incMyPitch();
    const s = pushHistory();
    const bi = myTeamBatting ? { playerId: currentBatter?.id, playerName: lastName(currentBatter?.name) } : { playerId: null, playerName: lastName(currentOppBatter?.name) };
    let runs = 0;
    let nr = { first: null, second: null, third: null };
    if (s.runners.third) runs++;
    if (s.runners.second) { if (bases >= 2) runs++; else nr.third = s.runners.second; }
    if (s.runners.first) { if (bases >= 3) runs++; else if (bases === 2) { if (nr.third) runs++; nr.third = s.runners.first; } else nr.second = s.runners.first; }
    if (bases === 1) nr.first = bi; else if (bases === 2) nr.second = bi; else if (bases === 3) nr.third = bi;
    s.runners = nr; addScore(s, runs);
    s.balls = 0; s.strikes = 0; s.currentBatterIndex = advanceBatter();
    save(s, `${bases}-base Error - ${batterName}${runs ? ` (${runs} R)` : ''}`);
    setHitDialog(false); setErrorDialog(false);
    if (myTeamBatting && currentBatter) setSprayDialog({ open: true, batterId: currentBatter.id, batterName: currentBatter.name, result: `error_${bases}`, rbi: runs, inning: s.inning });
  };

  // ===== OUT =====
  const handleOut = (state, desc) => {
    const s = state || pushHistory();
    if (!state) { if (myTeamBatting) incOppPitch(); else incMyPitch(); }
    s.outs += 1; s.balls = 0; s.strikes = 0;
    if (!checkThreeOuts(s, desc || `Out - ${batterName}`)) {
      s.currentBatterIndex = advanceBatter();
      const hasRunners = s.runners.first || s.runners.second || s.runners.third;
      if (hasRunners) {
        save(s, desc || `Out - ${batterName}`);
        setRunnerAdvanceDlg({ open: true, state: s, desc: desc || `Out - ${batterName}` });
      } else {
        save(s, desc || `Out - ${batterName}`);
      }
    }
    setOutDialog(false);
  };

  // ===== RUNNER ADVANCE AFTER OUT =====
  const handleRunnerAdvanceConfirm = (placements) => {
    const s = { ...runnerAdvanceDlg.state };
    let runs = 0;
    const nr = { first: null, second: null, third: null };
    ['first', 'second', 'third'].forEach(base => {
      const runner = s.runners[base];
      if (!runner) return;
      const dest = placements[base];
      if (!dest || dest === base) nr[base] = runner;
      else if (dest === 'home') runs++;
      else nr[dest] = runner;
    });
    s.runners = nr;
    addScore(s, runs);
    if (runs) save(s, `Runners advance after out (${runs} R)`);
    else save(s, 'Runners advance after out');
    setRunnerAdvanceDlg({ open: false, state: null, desc: '' });
  };

  // ===== SACRIFICE =====
  const handleSac = (runnerSafe = false) => {
    const hasRunners = gs.runners.first || gs.runners.second || gs.runners.third;
    if (!hasRunners) { toast.error('Need runners on base for SAC'); return; }
    const s = pushHistory();
    const bi = myTeamBatting ? { playerId: currentBatter?.id, playerName: lastName(currentBatter?.name) } : { playerId: null, playerName: lastName(currentOppBatter?.name) };
    if (!runnerSafe) {
      s.outs += 1; s.balls = 0; s.strikes = 0;
      if (s.outs >= 3) { checkThreeOuts(s, `Sacrifice - ${batterName}`); setHitDialog(false); return; }
    } else {
      s.balls = 0; s.strikes = 0;
    }
    s.currentBatterIndex = advanceBatter();
    const sp = {};
    if (gs.runners.third) sp.third = 'home';
    if (gs.runners.second) sp.second = 'third';
    if (gs.runners.first) sp.first = 'second';
    const desc = runnerSafe ? `SAC Bunt - ${batterName} safe at 1st` : `Sacrifice - ${batterName}`;
    setRunnerDialog({ open: true, state: s, desc, placements: sp, batterBase: runnerSafe ? 'first' : null, batterInfo: runnerSafe ? bi : null, isSac: true, hitType: null });
    setHitDialog(false);
  };

  // ===== FIELDER'S CHOICE =====
  const handleFC = (outBase) => {
    const s = pushHistory();
    const bi = myTeamBatting ? { playerId: currentBatter?.id, playerName: lastName(currentBatter?.name) } : { playerId: null, playerName: lastName(currentOppBatter?.name) };
    s.runners[outBase] = null; s.outs += 1; s.runners.first = bi;
    s.balls = 0; s.strikes = 0;
    if (!checkThreeOuts(s, `FC - ${batterName} safe 1st, out at ${outBase}`)) {
      s.currentBatterIndex = advanceBatter();
      save(s, `Fielder's Choice - ${batterName} safe 1st, out at ${outBase}`);
    }
    setFcDialog(false);
  };

  // ===== PICK OFF =====
  const handlePickOff = (base) => {
    const runner = gs.runners[base];
    if (!runner) { toast.error(`No runner on ${base}`); return; }
    const s = pushHistory();
    s.runners[base] = null; s.outs += 1;
    if (!checkThreeOuts(s, `Pick Off! Runner out at ${base}`, false)) {
      save(s, `Pick Off! Runner out at ${base}`);
    }
    setOutDialog(false);
  };

  // ===== DROPPED THIRD STRIKE =====
  const handleDropK = (throwOK, runnerBase) => {
    const s = pushHistory();
    if (throwOK) {
      s.outs += 1; s.balls = 0; s.strikes = 0;
      if (!checkThreeOuts(s, 'Drop 3rd Strike - Out at 1st')) {
        s.currentBatterIndex = advanceBatter();
        save(s, 'Drop 3rd Strike - Throw to 1st - Out!');
      }
    } else {
      const bi = myTeamBatting ? { playerId: currentBatter?.id, playerName: lastName(currentBatter?.name) } : { playerId: null, playerName: lastName(currentOppBatter?.name) };
      let runs = 0;
      if (runnerBase === 'first') {
        if (s.runners.first) { if (s.runners.second) { if (s.runners.third) runs++; s.runners.third = s.runners.second; } s.runners.second = s.runners.first; }
        s.runners.first = bi;
      } else if (runnerBase === 'second') { if (s.runners.third) runs++; if (s.runners.second) s.runners.third = s.runners.second; s.runners.second = bi; }
      addScore(s, runs);
      s.balls = 0; s.strikes = 0; s.currentBatterIndex = advanceBatter();
      save(s, `Drop 3rd Strike - Safe at ${runnerBase}${runs ? ` (${runs} R)` : ''}`);
    }
    setDropKDialog({ open: false, step: 'throw' });
  };

  // ===== WILD PITCH / PASSED BALL / BALK =====
  const handleAdvanceAll = (label) => {
    const s = pushHistory();
    let runs = 0;
    if (s.runners.third) { runs++; s.runners.third = null; }
    if (s.runners.second) { s.runners.third = s.runners.second; s.runners.second = null; }
    if (s.runners.first) { s.runners.second = s.runners.first; s.runners.first = null; }
    addScore(s, runs);
    save(s, `${label} - Runners advance${runs ? ` (${runs} R)` : ''}`);
    setWildPitchDialog(false);
  };

  // ===== STEAL / CAUGHT STEALING =====
  const handleCaughtOut = (base) => {
    const s = pushHistory();
    const runner = s.runners[base];
    const rn = runner?.playerName || base;
    s.runners[base] = null; s.outs += 1;
    if (!checkThreeOuts(s, `Caught Stealing - ${rn} out at ${base}`, false)) {
      save(s, `Caught Stealing - ${rn} out at ${base}`);
    }
    setStealDialog({ open: false, mode: 'steal', destinations: {}, caughtBase: null });
  };

  const handleStealConfirm = () => {
    const { caughtBase, mode } = stealDialog;
    if (mode === 'caught' && caughtBase) {
      handleCaughtOut(caughtBase);
      return;
    }
    const s = pushHistory();
    const { destinations } = stealDialog;
    const orig = { ...s.runners };
    const newRunners = { first: null, second: null, third: null };
    let runs = 0;
    const moves = [];

    // Build the list of all 3 bases. Process highest-first so non-stealers
    // who stay put don't collide with stealers being placed.
    ['third', 'second', 'first'].forEach(base => {
      const runner = orig[base];
      if (!runner) return;
      const dest = destinations[base];
      const name = runner.playerName || '?';
      if (!dest) {
        // No destination chosen — runner stays put.
        newRunners[base] = runner;
        return;
      }
      if (dest === 'home') {
        runs += 1;
        moves.push(`${name} ${base[0].toUpperCase()}${base.slice(1)}→Home`);
      } else if (dest === '2nd' && !newRunners.second) {
        newRunners.second = runner;
        moves.push(`${name} 1B→2B`);
      } else if (dest === '3rd' && !newRunners.third) {
        newRunners.third = runner;
        moves.push(`${name} ${base === 'first' ? '1B' : '2B'}→3B`);
      } else {
        // Conflict (rare — two runners picked the same destination).
        // Fall back to keeping them on their original base.
        newRunners[base] = runner;
      }
    });

    if (moves.length === 0) {
      toast.error('Pick at least one destination');
      return;
    }

    s.runners = newRunners;
    addScore(s, runs);
    save(s, `Stolen Base — ${moves.join(', ')}${runs ? ` (${runs} R)` : ''}`);
    setStealDialog({ open: false, mode: 'steal', destinations: {}, caughtBase: null });
  };

  // ===== RUNNER PLACEMENT CONFIRM =====
  const handleRunnerConfirm = () => {
    const { state: s, desc, placements, batterBase, batterInfo, isSac, hitType } = runnerDialog;
    let ns = { ...s };
    let runs = 0, outsAdded = 0;
    let nr = { first: null, second: null, third: null };
    ['first', 'second', 'third'].forEach(base => {
      const runner = ns.runners[base];
      if (!runner) return;
      const dest = placements[base];
      if (!dest || dest === base) nr[base] = runner;
      else if (dest === 'home') runs++;
      else if (dest === 'out') outsAdded++;
      else nr[dest] = runner;
    });
    if (batterBase && batterInfo) nr[batterBase] = batterInfo;
    ns.runners = nr;
    ns.outs += outsAdded;
    addScore(ns, runs);
    const wasThreeOuts = ns.outs >= 3;
    if (wasThreeOuts) { ns.outs = 0; ns.runners = { first: null, second: null, third: null }; if (ns.isTopInning) ns.isTopInning = false; else { ns.isTopInning = true; ns.inning += 1; } }
    const outNote = outsAdded > 0 ? `, ${outsAdded} out` : '';
    save(ns, `${desc}${runs ? ` (${runs} R)` : ''}${outNote}${wasThreeOuts ? ' - 3 Outs!' : ''}`, wasThreeOuts);
    setRunnerDialog({ open: false, state: null, desc: '', placements: {}, batterBase: null, batterInfo: null, isSac: false, hitType: null });
    if (!isSac && hitType && batterInfo?.playerId) setSprayDialog({ open: true, batterId: batterInfo.playerId, batterName: batterInfo.playerName, result: hitType, rbi: runs, inning: ns.inning });
  };

  // ===== FLYOUT ADVANCE =====
  const handleFlyoutConfirm = () => {
    const { state: s, desc, runners: rta } = flyoutDialog;
    let ns = { ...s }; let runs = 0;
    if (rta.third && ns.runners.third) { runs++; ns.runners.third = null; }
    if (rta.second && ns.runners.second) { ns.runners.third = ns.runners.second; ns.runners.second = null; }
    if (rta.first && ns.runners.first) { ns.runners.second = ns.runners.first; ns.runners.first = null; }
    addScore(ns, runs);
    save(ns, `${desc} → Tag-up${runs ? ` (${runs} R)` : ''}`);
    setFlyoutDialog({ open: false, state: null, desc: '', runners: {} });
  };

  // ===== DEFENSE MODE (tap positions) =====
  const handlePosTap = (pos) => {
    const seq = [...defenseSeq, pos];
    setDefenseSeq(seq);
    if (seq.length >= 2 && seq[seq.length-1] === seq[seq.length-2]) {
      // Flyout
      const s = pushHistory();
      s.outs += 1; s.balls = 0; s.strikes = 0;
      const desc = `Flyout (F-${getPN(pos)})`;
      if (s.outs >= 3) { checkThreeOuts(s, desc); }
      else {
        s.currentBatterIndex = advanceBatter();
        const hasRunners = s.runners.first || s.runners.second || s.runners.third;
        save(s, desc);
        if (hasRunners) setFlyoutDialog({ open: true, state: s, desc, runners: {} });
      }
      setDefenseSeq([]); setDefenseMode(false); return;
    }
    if (seq.length >= 3) {
      // Double play
      const s = pushHistory();
      s.outs += 2; if (s.runners.first) s.runners.first = null;
      s.balls = 0; s.strikes = 0;
      if (!checkThreeOuts(s, `Double Play! (${fmtSeq(seq)})`)) {
        s.currentBatterIndex = advanceBatter();
        save(s, `Double Play! (${fmtSeq(seq)})`);
      }
      setDefenseSeq([]); setDefenseMode(false); return;
    }
  };
  const confirmDefOut = () => {
    if (defenseSeq.length >= 2) {
      handleOut(null, `Out (${fmtSeq(defenseSeq)})`);
      setDefenseSeq([]); setDefenseMode(false);
    }
  };

  // ===== SPRAY CHART =====
  const handleSprayTap = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * 1000) / 10;
    const y = Math.round(((e.clientY - rect.top) / rect.height) * 1000) / 10;
    const ab = { batter_id: sprayDialog.batterId, batter_name: sprayDialog.batterName, result: sprayDialog.result, spray_x: x, spray_y: y, rbi: sprayDialog.rbi, inning: sprayDialog.inning };
    const updated = [...atBats, ab]; setAtBats(updated);
    updateGame(currentGameId, { at_bats: updated }).catch(()=>{});
    setSprayDialog({ open: false, batterId: null, batterName: '', result: '', rbi: 0, inning: 1 });
    toast.success('Hit location saved');
  };
  const skipSpray = () => {
    const ab = { batter_id: sprayDialog.batterId, batter_name: sprayDialog.batterName, result: sprayDialog.result, rbi: sprayDialog.rbi, inning: sprayDialog.inning };
    const updated = [...atBats, ab]; setAtBats(updated);
    updateGame(currentGameId, { at_bats: updated }).catch(()=>{});
    setSprayDialog({ open: false, batterId: null, batterName: '', result: '', rbi: 0, inning: 1 });
  };

  // ===== UNDO / REDO / UNDO-TO-HERE =====
  // Track consecutive undos within the same inning — after the 3rd, surface a
  // contextual nudge pointing the coach at the play-log tap-to-rewind feature.
  const undoNudgeRef = useRef({ inningKey: null, count: 0, shown: false });

  const handleUndo = () => {
    if (gs.playHistory.length === 0) { toast.error('Nothing to undo'); return; }
    const prev = gs.playHistory[gs.playHistory.length - 1];
    const undoneState = { gs: { ...gs }, playLog: [...playLog], oppIdx: oppIdxRef.current };
    setGs({ ...prev, playHistory: gs.playHistory.slice(0, -1) });
    if (prev._oppIdx !== undefined) setOppIdx(prev._oppIdx);
    const undoLog = playLog.length > 0 ? playLog.slice(0, -1) : playLog;
    const withUndo = [...undoLog, { id: Date.now(), description: 'UNDO - Last play reversed', inning: prev.inning, isTopInning: prev.isTopInning, outs: prev.outs, time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }];
    setPlayLog(withUndo);
    setRedoStack([...redoStack, undoneState]);
    closeAllDialogs();
    persistSnapshot(prev, withUndo, prev._oppIdx !== undefined ? prev._oppIdx : oppIdxRef.current);
    toast.success('Last play undone');

    // Smart contextual nudge — after 3 undos in one inning, point at play-log tap-to-rewind
    const inningKey = `${gs.inning}-${gs.isTopInning ? 'T' : 'B'}`;
    const ref = undoNudgeRef.current;
    if (ref.inningKey !== inningKey) {
      ref.inningKey = inningKey;
      ref.count = 1;
    } else {
      ref.count += 1;
    }
    if (ref.count >= 3 && !ref.shown) {
      ref.shown = true;
      toast.info(
        'Undoing a lot? Tap any row in the Play-by-Play log to rewind multiple plays at once.',
        { duration: 6000 }
      );
    }
  };

  const handleRedo = () => {
    if (redoStack.length === 0) { toast.error('Nothing to redo'); return; }
    const last = redoStack[redoStack.length - 1];
    setGs(last.gs);
    setPlayLog(last.playLog);
    if (last.oppIdx !== undefined) setOppIdx(last.oppIdx);
    setRedoStack(redoStack.slice(0, -1));
    persistSnapshot(last.gs, last.playLog, last.oppIdx !== undefined ? last.oppIdx : oppIdxRef.current);
    toast.success('Redone');
  };

  // Undo to a specific play-log index (everything AFTER that index is rolled back).
  // targetIndex is the index into the playLog array. We pop playHistory N times where N = entries after targetIndex.
  const handleUndoToIndex = (targetIndex) => {
    const count = playLog.length - 1 - targetIndex;
    if (count <= 0) { toast.error('That is the latest play'); return; }
    if (gs.playHistory.length < count) { toast.error("Can't roll back that far"); return; }
    const startHistory = gs.playHistory.slice(0, gs.playHistory.length - count);
    const targetSnapshot = gs.playHistory[gs.playHistory.length - count];
    const undoneState = { gs: { ...gs }, playLog: [...playLog], oppIdx: oppIdxRef.current };
    setGs({ ...targetSnapshot, playHistory: startHistory });
    if (targetSnapshot._oppIdx !== undefined) setOppIdx(targetSnapshot._oppIdx);
    const newLog = playLog.slice(0, targetIndex + 1);
    const withMarker = [...newLog, {
      id: Date.now(),
      description: `UNDO - Rolled back ${count} play${count === 1 ? '' : 's'}`,
      inning: targetSnapshot.inning, isTopInning: targetSnapshot.isTopInning,
      outs: targetSnapshot.outs,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }];
    setPlayLog(withMarker);
    setRedoStack([...redoStack, undoneState]);
    closeAllDialogs();
    persistSnapshot(targetSnapshot, withMarker, targetSnapshot._oppIdx !== undefined ? targetSnapshot._oppIdx : oppIdxRef.current);
    toast.success(`Rolled back ${count} play${count === 1 ? '' : 's'}`);
  };

  // ===== ADD RUNNER =====
  const handleAddRunner = () => {
    const { base, selected } = addRunnerDlg;
    if (!base || !selected) return;
    const s = pushHistory();
    s.runners = { ...s.runners, [base]: { playerId: selected.id, playerName: selected.name } };
    save(s, `Runner placed: ${selected.name} on ${base}`);
    setAddRunnerDlg({ open: false, base: null, selected: null });
  };

  // ===== TAP-BASE (diamond) =====
  const handleLoadRoster = () => {
    const ids = (team?.players || []).map(p => p.id);
    if (!ids.length) { toast.error('No players on your roster yet — add them on the Lineup page first'); return; }
    const s = pushHistory();
    s.battingOrder = ids;
    save(s, `Batting order loaded from roster (${ids.length} players)`);
  };
  const handleBaseTap = (base) => {
    if (gs.runners[base]) setBaseActionDlg({ open: true, base });
    else setAddRunnerDlg({ open: true, base, selected: null });
  };
  const advanceRunnerTo = (base, dest) => {
    const s = pushHistory();
    const runner = s.runners[base];
    if (!runner) return;
    if (dest !== 'home' && s.runners[dest]) { toast.error(`${dest} base is occupied`); return; }
    const lbl = { first: '1B', second: '2B', third: '3B', home: 'Home' };
    s.runners = { ...s.runners, [base]: null };
    let desc;
    if (dest === 'home') { addScore(s, 1); desc = `${runner.playerName} scores from ${lbl[base]}! (1 R)`; }
    else { s.runners[dest] = runner; desc = `${runner.playerName} advances ${lbl[base]} → ${lbl[dest]}`; }
    save(s, desc);
    setBaseActionDlg({ open: false, base: null });
  };
  const removeRunnerFromBase = (base) => {
    const s = pushHistory();
    const name = s.runners[base]?.playerName || '?';
    s.runners = { ...s.runners, [base]: null };
    save(s, `Correction: ${name} removed from ${base}`);
    setBaseActionDlg({ open: false, base: null });
  };

  // ===== SUBSTITUTION =====
  const handleSubConfirm = () => {
    const { type, playerIndex, playerId, selected } = subDialog;
    if (playerIndex === null || !selected) return;
    const oldP = team?.players?.find(p => p.id === playerId);
    const newP = team?.players?.find(p => p.id === selected);
    const newOrder = [...gs.battingOrder];
    newOrder[playerIndex] = selected;
    const s = { ...gs, battingOrder: newOrder, playHistory: [...gs.playHistory, { ...gs, _oppIdx: oppIdxRef.current }] };
    save(s, `${type === 'pinch' ? 'Pinch Hitter' : type === 'injury' ? 'Injury Sub' : 'Sub'}: ${newP?.name} for ${oldP?.name} at #${playerIndex + 1}`);
    setSubDialog({ open: false, type: '', playerIndex: null, playerId: null, selected: null });
    toast.success(`${newP?.name} in for ${oldP?.name}`);
  };

  // ===== SCORE ADJUST =====
  const adjScore = (side, delta) => {
    const s = { ...gs }; if (side === 'away') s.awayScore = Math.max(0, s.awayScore + delta); else s.homeScore = Math.max(0, s.homeScore + delta);
    setGs(s); save(s, `Score adjusted: ${side} ${delta > 0 ? '+' : ''}${delta}`);
  };

  // ===== KEYBOARD SHORTCUTS (refs prevent stale closures) =====
  const kbdHandlersRef = useRef({});
  kbdHandlersRef.current = { handleBall, handleStrike, handleFoul, handleUndo, handleRedo, setHitDialog, setOutDialog, gs, hitDialog, outDialog, errorDialog, wildPitchDialog, fcDialog, rareDialog, catcherBalkDialog, changePitcherDialog, stealDialog, runnerDialog, sprayDialog, flyoutDialog, dropKDialog, subDialog, addRunnerDlg, restWarnDialog, runnerAdvanceDlg, phDialog, undoToDialog, endGameDialog, editOppPitcherDlg, baseActionDlg, rosterPromptDlg };

  useEffect(() => {
    const anyDialogOpen = () => {
      const h = kbdHandlersRef.current;
      return h.hitDialog || h.outDialog || h.errorDialog || h.wildPitchDialog || h.fcDialog || h.rareDialog || h.catcherBalkDialog || h.changePitcherDialog ||
        h.stealDialog?.open || h.runnerDialog?.open || h.sprayDialog?.open || h.flyoutDialog?.open || h.dropKDialog?.open ||
        h.subDialog?.open || h.addRunnerDlg?.open || h.restWarnDialog?.open || h.runnerAdvanceDlg?.open || h.phDialog?.open ||
        h.undoToDialog?.open || h.endGameDialog || h.editOppPitcherDlg?.open || h.baseActionDlg?.open || h.rosterPromptDlg;
    };
    const onKey = (e) => {
      // Ignore when typing in inputs / editable areas
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      const h = kbdHandlersRef.current;
      // Undo / Redo shortcuts (work even with some dialogs open; dialog close happens inside handleUndo)
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) h.handleRedo(); else h.handleUndo();
        return;
      }
      if (anyDialogOpen()) return; // block single-key shortcuts while a dialog is up
      const key = e.key.toLowerCase();
      if (key === 'b') { e.preventDefault(); h.handleBall(); return; }
      if (key === 's') { e.preventDefault(); h.handleStrike(); return; }
      if (key === 'f') { e.preventDefault(); if (h.gs.strikes >= 2) h.handleFoul(); return; }
      if (key === 'u') { e.preventDefault(); h.handleUndo(); return; }
      if (key === 'h') { e.preventDefault(); h.setHitDialog(true); return; }
      if (key === 'o') { e.preventDefault(); h.setOutDialog(true); return; }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const adjInning = (delta) => {
    const s = { ...gs };
    if (delta > 0) {
      if (s.isTopInning) { s.isTopInning = false; } else { s.isTopInning = true; s.inning = Math.max(1, s.inning + 1); }
    } else {
      if (!s.isTopInning) { s.isTopInning = true; } else { s.inning = Math.max(1, s.inning - 1); s.isTopInning = false; }
    }
    setGs(s);
    save(s, `Inning → ${s.isTopInning ? 'TOP' : 'BOT'} ${s.inning}`);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0A1322]"><div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen p-2 md:p-4 pb-16" style={{ background: 'radial-gradient(ellipse at 50% 0%, #101C33 0%, #0A1322 60%)' }} data-testid="game-mode-page">
      <QuickTipsButton tips={GAME_MODE_TIPS} tour={GAME_MODE_TOUR} storageKey="ds_quicktips_seen_gamemode_v3" />
      <GoLiveButton
        gameId={currentGameId}
        currentUrl={game?.live_stream_url || ''}
        currentActive={!!game?.live_stream_active}
        streamHistory={game?.stream_history || []}
        onSaved={(patch) => setGame((g) => g ? { ...g, ...patch } : g)}
      />
      <LightningDelayButton
        gameId={currentGameId}
        opponentName={game?.opponent_name}
        lightningDelay={game?.lightning_delay}
        onSaved={(patch) => setGame((g) => g ? { ...g, ...patch } : g)}
      />
      <div className="max-w-2xl mx-auto space-y-2 pt-12 md:pt-2">
        {/* === BROADCAST SCORE BUG === */}
        <div className="rounded-2xl border border-white/10 shadow-2xl overflow-hidden" style={{ background: '#131E36' }} data-testid="game-mode-scoreboard">
          <div className="flex items-stretch">
            <div className={cn("flex-1 min-w-0 text-center px-1 py-2", gs.isTopInning && "bg-[#1A2542]")}>
              <p className="text-slate-500 text-[9px] uppercase tracking-widest">AWAY</p>
              <p className="text-[10px] font-bold text-slate-300 truncate px-1">{isHome ? oppName : teamName}</p>
              <div className="flex items-center justify-center gap-1.5">
                <button onClick={() => adjScore('away', -1)} className="w-6 h-6 rounded-full bg-white/5 border border-white/10 text-slate-400 text-xs" data-testid="gm-away-score-minus">-</button>
                <p className="text-4xl font-black min-w-[2ch]" style={{ fontFamily: 'Barlow Condensed, sans-serif', color: gs.isTopInning ? '#F5C455' : '#fff' }}>{isHome ? oppScore : myScore}</p>
                <button onClick={() => adjScore('away', 1)} className="w-6 h-6 rounded-full bg-white/5 border border-white/10 text-slate-400 text-xs" data-testid="gm-away-score-plus">+</button>
              </div>
            </div>
            <div className="px-2 py-2 text-center border-x border-white/10 shrink-0 flex flex-col items-center justify-center gap-1">
              <div className="flex items-center gap-1">
                <button onClick={() => adjInning(-1)} className="w-5 h-5 rounded-full bg-white/5 border border-white/10 text-slate-400 text-[10px]">-</button>
                <button onClick={toggleHalf} className="font-black text-sm px-1 hover:opacity-80" style={{ fontFamily: 'Barlow Condensed, sans-serif', color: '#F5C455' }} data-testid="inning-indicator" title="Tap to flip half">
                  {gs.isTopInning ? '▲ TOP' : '▼ BOT'} {gs.inning}
                </button>
                <button onClick={() => adjInning(1)} className="w-5 h-5 rounded-full bg-white/5 border border-white/10 text-slate-400 text-[10px]">+</button>
              </div>
              <p className="text-xl font-black text-white leading-none" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="gm-count-display">{gs.balls}-{gs.strikes}</p>
              <div className="flex items-center gap-1.5">
                <button className="w-5 h-5 rounded-full bg-white/5 border border-white/10 text-slate-400 text-[10px]" onClick={() => {
                  const s = { ...gs, outs: Math.max(0, gs.outs - 1) }; setGs(s); save(s, `Outs adjusted to ${s.outs}`);
                }} data-testid="outs-minus">-</button>
                <div className="flex gap-1" data-testid="gm-outs-dots" title={`${gs.outs} out${gs.outs !== 1 ? 's' : ''}`}>
                  {[0,1,2].map(i => (
                    <span key={i} className={cn("w-2.5 h-2.5 rounded-full", i < gs.outs ? "bg-red-500 shadow-[0_0_6px_rgba(239,68,68,0.8)]" : "border border-white/25")} />
                  ))}
                </div>
                <button className="w-5 h-5 rounded-full bg-white/5 border border-white/10 text-slate-400 text-[10px]" onClick={() => {
                  const s = { ...gs, outs: Math.min(3, gs.outs + 1) }; setGs(s); save(s, `Outs adjusted to ${s.outs}`);
                }} data-testid="outs-plus">+</button>
              </div>
            </div>
            <div className={cn("flex-1 min-w-0 text-center px-1 py-2", !gs.isTopInning && "bg-[#1A2542]")}>
              <p className="text-slate-500 text-[9px] uppercase tracking-widest">HOME</p>
              <p className="text-[10px] font-bold text-slate-300 truncate px-1">{isHome ? teamName : oppName}</p>
              <div className="flex items-center justify-center gap-1.5">
                <button onClick={() => adjScore('home', -1)} className="w-6 h-6 rounded-full bg-white/5 border border-white/10 text-slate-400 text-xs" data-testid="gm-home-score-minus">-</button>
                <p className="text-4xl font-black min-w-[2ch]" style={{ fontFamily: 'Barlow Condensed, sans-serif', color: !gs.isTopInning ? '#F5C455' : '#fff' }}>{isHome ? myScore : oppScore}</p>
                <button onClick={() => adjScore('home', 1)} className="w-6 h-6 rounded-full bg-white/5 border border-white/10 text-slate-400 text-xs" data-testid="gm-home-score-plus">+</button>
              </div>
            </div>
          </div>
        </div>

        {/* === HALF INDICATOR === */}
        <div className={cn("text-center py-1.5 rounded-xl font-bold text-xs tracking-widest border", myTeamBatting ? "bg-emerald-900/30 text-emerald-300 border-emerald-700/40" : "bg-red-900/25 text-red-300 border-red-700/40")}
          data-testid="half-indicator" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          {myTeamBatting ? <span className="flex items-center justify-center gap-1.5"><Swords size={14} /> {teamName} BATTING</span>
            : <span className="flex items-center justify-center gap-1.5"><Shield size={14} /> {oppName} BATTING</span>}
        </div>

        {/* === CENTER-STAGE DIAMOND === */}
        <div className="rounded-2xl border border-white/10 px-1 pt-1 pb-1.5 relative overflow-hidden" style={{ background: 'radial-gradient(ellipse at 50% 15%, #101C33 0%, #0A1322 75%)' }} data-testid="gm-diamond-card">
          <BroadcastDiamond
            runners={gs.runners}
            batterName={batterName}
            batterOrderNum={(myTeamBatting ? gs.currentBatterIndex : oppBatterIndex) + 1}
            pitcherName={myTeamBatting ? (oppPitcherName || null) : (myPitcher?.name || null)}
            pitchCount={myTeamBatting ? oppPitchCount : myPitchCount}
            pitchLimit={!myTeamBatting ? myPitchLimit : null}
            isMyTeamPitching={!myTeamBatting}
            onBaseTap={handleBaseTap}
            onPitcherTap={() => { if (myTeamBatting) setEditOppPitcherDlg({ open: true, name: oppPitcherName || '' }); else setChangePitcherDialog(true); }}
            onPitchAdjust={(d) => {
              if (myTeamBatting) { const c = Math.max(0, oppPitchCount + d); setOppPitchCount(c); updateGame(currentGameId, { opponent_pitch_count: c }).catch(() => {}); }
              else { const c = Math.max(0, myPitchCount + d); setMyPitchCount(c); updateGame(currentGameId, { pitch_count: c }).catch(() => {}); }
            }}
          />
          <p className="text-slate-500 text-[9px] text-center">Tap an empty base to place a runner · tap a runner to advance, score or fix</p>
        </div>

        {/* === RUNNER ACTIONS (contextual — only when runners on) === */}
        {(gs.runners.first || gs.runners.second || gs.runners.third) && (
          <div className="grid grid-cols-3 gap-2" data-testid="runner-actions">
            <Button className="h-11 bg-amber-700 hover:bg-amber-600 text-xs font-bold rounded-xl" onClick={() => setStealDialog({ open: true, mode: 'steal', destinations: {}, caughtBase: null })} data-testid="gm-steal-btn">
              SB Steal
            </Button>
            <Button className="h-11 bg-amber-800 hover:bg-amber-700 text-xs font-bold rounded-xl" onClick={() => setStealDialog({ open: true, mode: 'caught', destinations: {}, caughtBase: null })} data-testid="gm-cs-btn">
              CS Caught
            </Button>
            <Button className="h-11 bg-[#1A2542] hover:bg-[#233052] border border-white/15 text-slate-200 text-xs font-bold rounded-xl" onClick={() => setWildPitchDialog(true)} data-testid="gm-wp-btn">
              WP / PB / BK
            </Button>
          </div>
        )}

        {/* === PITCH BUTTONS === */}
        <div className="grid grid-cols-2 gap-2">
          <Button className="h-14 bg-[#1A2542] hover:bg-[#233052] border-2 border-emerald-500/50 text-emerald-300 font-black text-lg rounded-2xl tracking-wider" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} onClick={handleBall} disabled={saving} data-testid="gm-ball-btn" title="Shortcut: B">
            BALL ({gs.balls})
          </Button>
          <Button className="h-14 bg-[#1A2542] hover:bg-[#233052] border-2 border-red-500/50 text-red-300 font-black text-lg rounded-2xl tracking-wider" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} onClick={handleStrike} disabled={saving} data-testid="gm-strike-btn" title="Shortcut: S">
            STRIKE ({gs.strikes})
          </Button>
        </div>
        <p className="text-stone-600 text-[9px] text-center -mt-1 hidden sm:block">Keys: B=ball, S=strike, F=foul, H=hit, O=out, U=undo, ⌘/ctrl+Z=undo, ⌘/ctrl+⇧+Z=redo</p>

        {/* === FOUL BALL (visible at 2 strikes) === */}
        {gs.strikes >= 2 && (
          <Button className="w-full h-12 bg-[#1A2542] hover:bg-[#233052] border-2 border-amber-500/50 text-amber-300 font-black text-base rounded-2xl tracking-wider" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} onClick={handleFoul} disabled={saving} data-testid="gm-foul-btn">
            FOUL BALL (ADD PITCH)
          </Button>
        )}

        {/* === MAIN ACTION BUTTONS === */}
        <div className="grid grid-cols-2 gap-2">
          <Button className="h-16 bg-[#0E6B36] hover:bg-[#12864A] border-2 border-[#2EBE6B] font-black text-xl rounded-2xl tracking-wider" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} onClick={() => setHitDialog(true)} disabled={saving} data-testid="gm-hits-btn">
            HITS / REACH
          </Button>
          <Button className="h-16 bg-[#3B1520] hover:bg-[#4A1A28] border-2 border-[#E05265]/70 text-[#FFD9DE] font-black text-xl rounded-2xl tracking-wider" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} onClick={() => setOutDialog(true)} disabled={saving} data-testid="gm-outs-btn">
            OUTS
          </Button>
        </div>

        {/* === DEFENSE MODE === */}
        {defenseMode ? (
          <div className="bg-[#131E36] border border-white/10 rounded-xl p-3 space-y-2" data-testid="defense-mode">
            <p className="text-amber-400 text-xs font-bold text-center" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              TAP POSITIONS: {defenseSeq.map(p => getPN(p)).join(' → ') || 'Tap who fielded it...'}
            </p>
            {/* Diamond-shaped field layout */}
            <svg viewBox="0 0 240 180" className="w-full max-w-[320px] mx-auto">
              {/* Field grass background */}
              <path d="M 10 170 Q 10 10, 120 10 Q 230 10, 230 170" fill="#14532d" opacity="0.3" />
              {/* Basepaths */}
              <line x1="120" y1="155" x2="185" y2="100" stroke="rgba(255,255,255,0.15)" strokeWidth="0.5" />
              <line x1="185" y1="100" x2="120" y2="50" stroke="rgba(255,255,255,0.15)" strokeWidth="0.5" />
              <line x1="120" y1="50" x2="55" y2="100" stroke="rgba(255,255,255,0.15)" strokeWidth="0.5" />
              <line x1="55" y1="100" x2="120" y2="155" stroke="rgba(255,255,255,0.15)" strokeWidth="0.5" />
              {/* Position buttons */}
              {[
                { pos: 'LF', x: 35, y: 45 }, { pos: 'CF', x: 120, y: 20 }, { pos: 'RF', x: 205, y: 45 },
                { pos: 'SS', x: 80, y: 80 }, { pos: '2B', x: 160, y: 80 },
                { pos: '3B', x: 50, y: 115 }, { pos: 'P', x: 120, y: 100 }, { pos: '1B', x: 190, y: 115 },
                { pos: 'C', x: 120, y: 155 }
              ].map(({ pos, x, y }) => {
                const isInSeq = defenseSeq.includes(pos);
                return (
                  <g key={pos} onClick={() => handlePosTap(pos)} className="cursor-pointer" data-testid={`def-pos-${pos}`}>
                    <circle cx={x} cy={y} r={16} fill={isInSeq ? '#d97706' : '#44403c'} stroke={isInSeq ? '#fbbf24' : '#78716c'} strokeWidth="1.5" />
                    <text x={x} y={y + 1} textAnchor="middle" dominantBaseline="middle" fill="white" fontSize="10" fontWeight="bold">{pos}</text>
                  </g>
                );
              })}
            </svg>
            <div className="flex gap-2">
              {defenseSeq.length >= 2 && <Button size="sm" className="flex-1 bg-green-700 hover:bg-green-600 text-xs font-bold" onClick={confirmDefOut} data-testid="confirm-def-out">Record Out ({fmtSeq(defenseSeq)})</Button>}
              <Button size="sm" variant="outline" className="flex-1 border-stone-600 text-stone-400 text-xs" onClick={() => { setDefenseMode(false); setDefenseSeq([]); }}>Cancel</Button>
            </div>
            <p className="text-stone-600 text-[9px] text-center leading-relaxed">
              Tap 2 = out (e.g. <span className="text-amber-500 font-semibold">3-1</span> when 1B fields and pitcher covers, <span className="text-amber-500 font-semibold">3-4</span> when 1B fields and 2B covers, 6-3 etc.) · Tap 3 = double play · Tap same twice = flyout
            </p>
          </div>
        ) : (
          <Button variant="outline" className="w-full border-stone-700 text-stone-400 hover:text-white text-xs h-9 rounded-xl" onClick={() => setDefenseMode(true)} data-testid="gm-defense-btn">
            <Target size={14} className="mr-1" /> Record Defensive Play (tap positions)
          </Button>
        )}

        {/* === UTILITIES === */}
        <div className="flex gap-1.5 flex-wrap">
          <Button size="sm" variant="outline" className="border-stone-700 text-stone-400 text-[10px] rounded-lg" onClick={handleUndo} data-testid="gm-undo-btn"><RotateCcw size={12} className="mr-1" /> Undo</Button>
          {redoStack.length > 0 && (
            <Button size="sm" variant="outline" className="border-emerald-700 text-emerald-400 text-[10px] rounded-lg" onClick={handleRedo} data-testid="gm-redo-btn">
              <RotateCcw size={12} className="mr-1 scale-x-[-1]" /> Redo
            </Button>
          )}
          <Button size="sm" variant="outline" className="border-amber-700 text-amber-400 text-[10px] rounded-lg" onClick={handleChangeSide} data-testid="gm-change-side-btn"><Repeat size={12} className="mr-1" /> Change Side?</Button>
          <Button size="sm" variant="outline" className="border-stone-700 text-stone-400 text-[10px] rounded-lg" onClick={() => setAddRunnerDlg({ open: true, base: null, selected: null })}><UserPlus size={12} className="mr-1" /> Place Runner</Button>
          {team?.sport_type === 'softball' && (
            <Button
              size="sm"
              variant="outline"
              className="border-amber-700 text-amber-400 text-[10px] rounded-lg"
              onClick={() => setSoftballRulesDialog(true)}
              data-testid="gm-softball-rules-btn"
            >
              <BookOpen size={12} className="mr-1" /> Softball Rules
            </Button>
          )}
          <Button size="sm" variant="outline" className="border-stone-700 text-stone-400 text-[10px] rounded-lg" data-testid="gm-share-btn" onClick={() => {
            const url = `${window.location.origin}/live-score/${currentTeamId}/${currentGameId}`;
            if (navigator.share) { navigator.share({ title: `${teamName} vs ${oppName} - Live`, url }).catch(() => {}); }
            else { navigator.clipboard.writeText(url); toast.success('Live score link copied!'); }
          }}><Share2 size={12} className="mr-1" /> Share</Button>
          <Button size="sm" variant="outline" className="border-red-800 text-red-400 text-[10px] rounded-lg" data-testid="gm-end-game-btn" onClick={() => setEndGameDialog(true)}><X size={12} className="mr-1" /> End Game</Button>
        </div>

        {/* === PITCH COUNTS === */}
        <div className="flex gap-2">
          <div className="flex-1 bg-[#131E36] border border-white/10 rounded-lg p-2 flex items-center justify-between">
            <button
              className="text-stone-500 text-[10px] truncate text-left hover:text-amber-400 transition-colors"
              onClick={() => setEditOppPitcherDlg({ open: true, name: oppPitcherName || '' })}
              data-testid="gm-edit-opp-pitcher-btn"
              title="Tap to set opponent pitcher name"
            >
              {oppPitcherName || <span className="italic text-stone-600">+ Set Opp Pitcher</span>}
            </button>
            <div className="flex items-center gap-1">
              <button onClick={() => {
                const c = Math.max(0, oppPitchCount - 1);
                setOppPitchCount(c);
                updateGame(currentGameId, { opponent_pitch_count: c }).catch(() => {});
              }} className="w-5 h-5 rounded bg-stone-700 text-stone-400 text-[10px]" data-testid="gm-opp-pitch-minus">-</button>
              <span className="text-white font-bold text-xs min-w-[24px] text-center" data-testid="gm-opp-pitch-count">{oppPitchCount}</span>
              <button onClick={() => {
                const c = oppPitchCount + 1;
                setOppPitchCount(c);
                updateGame(currentGameId, { opponent_pitch_count: c }).catch(() => {});
              }} className="w-5 h-5 rounded bg-stone-700 text-stone-400 text-[10px]" data-testid="gm-opp-pitch-plus">+</button>
            </div>
          </div>
          <div className="flex-1 bg-[#131E36] border border-white/10 rounded-lg p-2 flex items-center justify-between">
            <span className="text-stone-500 text-[10px]">{myPitcher?.name || 'My Pitcher'}</span>
            <div className="flex items-center gap-1">
              <button onClick={() => {
                const c = Math.max(0, myPitchCount - 1);
                setMyPitchCount(c);
                updateGame(currentGameId, { pitch_count: c }).catch(() => {});
              }} className="w-5 h-5 rounded bg-stone-700 text-stone-400 text-[10px]" data-testid="gm-my-pitch-minus">-</button>
              <span className="text-white font-bold text-xs min-w-[24px] text-center" data-testid="gm-my-pitch-count">{myPitchCount}</span>
              <button onClick={() => {
                const c = myPitchCount + 1;
                setMyPitchCount(c);
                updateGame(currentGameId, { pitch_count: c }).catch(() => {});
              }} className="w-5 h-5 rounded bg-stone-700 text-stone-400 text-[10px]" data-testid="gm-my-pitch-plus">+</button>
            </div>
          </div>
        </div>

        {/* === PLAY LOG === */}
        <div className="bg-[#131E36]/80 border border-white/10 rounded-xl p-3 max-h-36 overflow-y-auto" ref={playLogRef} data-testid="gm-play-log">
          <p className="text-stone-500 text-[9px] font-bold mb-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>PLAY-BY-PLAY <span className="text-stone-600 font-normal">· tap a row to rewind</span></p>
          {playLog.length === 0 ? <p className="text-stone-600 text-xs text-center py-2">No plays yet</p> : (
            [...playLog].reverse().slice(0, 30).map((e, i) => {
              // actual forward index of this entry in playLog
              const reversedAll = [...playLog].reverse();
              const realIndex = playLog.length - 1 - reversedAll.indexOf(e);
              const playsAfter = playLog.length - 1 - realIndex;
              const isUndoMarker = typeof e.description === 'string' && e.description.startsWith('UNDO');
              const tappable = !isUndoMarker && playsAfter > 0;
              return (
                <button
                  key={e.id || i}
                  type="button"
                  className={cn(
                    "w-full flex gap-2 py-0.5 border-b border-stone-700/30 last:border-0 text-left transition-colors",
                    tappable ? "hover:bg-stone-700/40 cursor-pointer" : "cursor-default"
                  )}
                  onClick={() => {
                    if (!tappable) return;
                    setUndoToDialog({ open: true, targetIndex: realIndex, count: playsAfter });
                  }}
                  data-testid={`gm-playlog-row-${realIndex}`}
                  disabled={!tappable}
                >
                  <span className="text-amber-400 text-[9px] w-8 shrink-0">{e.time}</span>
                  <span className={cn("text-[10px]", isUndoMarker ? "text-amber-500 italic" : "text-stone-300")}>{e.description}</span>
                </button>
              );
            })
          )}
        </div>

        {/* === CHANGE PITCHER / QUICK SUB === */}
        <div className="grid grid-cols-3 gap-2">
          <Button size="sm" className="bg-blue-700 hover:bg-blue-600 text-white font-bold text-xs rounded-lg border-2 border-blue-500" onClick={() => setChangePitcherDialog(true)} data-testid="gm-change-pitcher-btn">
            Change Pitcher
          </Button>
          <Button size="sm" className="bg-amber-600 hover:bg-amber-500 text-white font-bold text-xs rounded-lg border-2 border-amber-400" onClick={() => { setQuickSubTab(myTeamBatting ? 'my' : 'opp'); setQuickSubOpen(true); }} data-testid="gm-quick-sub-btn">
            Quick Sub
          </Button>
          <Button size="sm" className="bg-stone-700 hover:bg-stone-600 text-white font-bold text-xs rounded-lg border-2 border-stone-500" onClick={() => navigate('/pitching')} data-testid="gm-pitching-btn">
            Pitchers
          </Button>
        </div>

        {/* === LINEUPS (always visible) === */}
        <div className="bg-[#131E36]/60 border border-white/10 rounded-xl">
          <div className="flex items-center justify-between p-2">
            <p className="text-stone-400 text-xs font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>LINEUPS</p>
            <button
              onClick={() => setBenchModeOpen(true)}
              className="flex items-center gap-1 text-[10px] font-bold tracking-wider text-emerald-300 hover:text-emerald-200 bg-emerald-900/40 hover:bg-emerald-900/60 px-2 py-1 rounded-md border border-emerald-700/40 uppercase transition-colors"
              data-testid="gm-bench-mode-btn"
            >
              <Users size={11} /> Bench Mode
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2 p-3 pt-0">
            <div>
              <p className="text-green-400 text-[9px] font-bold mb-1">{teamName}</p>
              {gs.battingOrder.length === 0 && (
                <div className="py-1" data-testid="gm-empty-lineup">
                  <p className="text-stone-500 text-[10px] mb-1.5">No batting order set for this game.</p>
                  <Button size="sm" className="h-9 w-full bg-emerald-700 hover:bg-emerald-600 text-[10px] font-bold rounded-lg" onClick={handleLoadRoster} data-testid="gm-load-roster-btn">
                    LOAD FULL ROSTER
                  </Button>
                  <button className="w-full text-center text-[9px] text-stone-500 hover:text-emerald-300 mt-1.5 underline" onClick={() => navigate('/lineup')} data-testid="gm-goto-lineup-btn">
                    or set the order on the Lineup page
                  </button>
                </div>
              )}
              {gs.battingOrder.map((pid, i) => {
                const p = team?.players?.find(pl => pl.id === pid);
                const isActive = i === gs.currentBatterIndex && myTeamBatting;
                return (
                  <div key={pid} className={cn("flex items-center gap-1 py-0.5 text-[10px] group", isActive ? "text-amber-400 font-bold" : "text-stone-500")}>
                    <span className="w-3">{i+1}</span>
                    <span className="truncate flex-1 cursor-pointer hover:text-white" onClick={() => handleChangeBatter(i)}>{p?.name || '?'}</span>
                    <button className="text-[8px] text-stone-600 hover:text-amber-400 opacity-0 group-hover:opacity-100" onClick={() => setPhDialog({ open: true, playerIndex: i, playerId: pid, newName: '' })} data-testid={`ph-btn-${i}`}>PH</button>
                  </div>
                );
              })}
            </div>
            <div>
              <p className="text-red-400 text-[9px] font-bold mb-1">{oppName}</p>
              {oppLineup.length === 0 && (
                <p className="text-stone-600 text-[9px] py-1" data-testid="gm-empty-opp-lineup">No opponent lineup (optional) — add it in Game Setup.</p>
              )}
              {oppLineup.map((opp, i) => {
                const isActive = i === oppBatterIndex && !myTeamBatting;
                return (
                  <div key={i} className={cn("flex items-center gap-1 py-0.5 text-[10px]", isActive ? "text-amber-400 font-bold" : "text-stone-500")}>
                    <span className="w-3">{i+1}</span>
                    <span className="truncate cursor-pointer hover:text-white" onClick={() => handleChangeBatter(i)}>{opp.name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ======== DIALOGS ======== */}

      {/* BENCH MODE */}
      <BenchModeDialog
        open={benchModeOpen}
        onOpenChange={setBenchModeOpen}
        team={team}
        game={game}
        gs={gs}
        currentInning={gs.inning}
        teamName={teamName}
      />

      {/* END GAME CONFIRM */}
      <Dialog open={endGameDialog} onOpenChange={setEndGameDialog}>
        <DialogContent className="bg-stone-800 border-red-800 max-w-xs" data-testid="gm-end-game-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-400 text-center text-sm flex items-center justify-center gap-2">
              <X size={16} /> End Game?
            </DialogTitle>
            <DialogDescription className="sr-only">Confirm ending the game and navigate to the summary.</DialogDescription>
          </DialogHeader>
          <div className="text-stone-300 text-xs text-center pt-2">
            <div className="bg-stone-900/60 rounded-lg p-3 my-2">
              <div className="flex items-center justify-center gap-4 text-sm">
                <div className="text-right">
                  <p className="text-stone-400 text-[10px] uppercase">{teamName}</p>
                  <p className="text-2xl font-extrabold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{myScore}</p>
                </div>
                <span className="text-stone-600 text-xs">vs</span>
                <div className="text-left">
                  <p className="text-stone-400 text-[10px] uppercase">{oppName}</p>
                  <p className="text-2xl font-extrabold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{oppScore}</p>
                </div>
              </div>
              <p className="text-stone-500 text-[10px] mt-1">Final · Inning {gs.inning} {gs.isTopInning ? 'TOP' : 'BOT'}</p>
            </div>
            You can still view the game summary afterwards.
          </div>
          <div className="flex gap-2 mt-2">
            <Button
              className="flex-1 bg-stone-700 hover:bg-stone-600 text-white font-bold text-xs"
              onClick={() => setEndGameDialog(false)}
              data-testid="gm-end-game-cancel"
            >
              Keep Playing
            </Button>
            <Button
              className="flex-1 bg-red-700 hover:bg-red-600 text-white font-bold text-xs"
              onClick={() => {
                setEndGameDialog(false);
                updateGame(currentGameId, { is_complete: true }).then(() => {
                  toast.success('Game ended!');
                  navigate(`/game-summary/${currentTeamId}/${currentGameId}`);
                }).catch(() => toast.error('Failed to end game'));
              }}
              data-testid="gm-end-game-confirm"
            >
              End Game
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* EDIT OPPONENT PITCHER NAME */}
      <Dialog open={editOppPitcherDlg.open} onOpenChange={(o) => { if (!o) setEditOppPitcherDlg({ open: false, name: '' }); }}>
        <DialogContent className="bg-stone-800 border-stone-700 max-w-xs" data-testid="gm-edit-opp-pitcher-dialog">
          <DialogHeader>
            <DialogTitle className="text-white text-center text-sm">Opponent Pitcher Name</DialogTitle>
            <DialogDescription className="sr-only">Set or edit the opponent pitcher name.</DialogDescription>
          </DialogHeader>
          <div className="pt-2">
            <Input
              value={editOppPitcherDlg.name}
              onChange={(e) => setEditOppPitcherDlg({ ...editOppPitcherDlg, name: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const name = editOppPitcherDlg.name.trim();
                  setOppPitcherName(name);
                  updateGame(currentGameId, { opponent_pitcher_name: name }).catch(() => {});
                  setEditOppPitcherDlg({ open: false, name: '' });
                  if (name) toast.success(`Opp pitcher: ${name}`);
                }
              }}
              placeholder="e.g., #17 Johnson"
              className="bg-stone-700 border-stone-600 text-white"
              data-testid="gm-opp-pitcher-input"
              autoFocus
            />
          </div>
          <div className="flex gap-2 mt-2">
            <Button
              className="flex-1 bg-stone-700 hover:bg-stone-600 text-white text-xs"
              onClick={() => setEditOppPitcherDlg({ open: false, name: '' })}
              data-testid="gm-opp-pitcher-cancel"
            >
              Cancel
            </Button>
            <Button
              className="flex-1 bg-amber-600 hover:bg-amber-500 text-white font-bold text-xs"
              onClick={() => {
                const name = editOppPitcherDlg.name.trim();
                setOppPitcherName(name);
                updateGame(currentGameId, { opponent_pitcher_name: name }).catch(() => {});
                setEditOppPitcherDlg({ open: false, name: '' });
                if (name) toast.success(`Opp pitcher: ${name}`);
              }}
              data-testid="gm-opp-pitcher-save"
            >
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* UNDO TO HERE CONFIRM DIALOG */}
      <Dialog open={undoToDialog.open} onOpenChange={(o) => { if (!o) setUndoToDialog({ open: false, targetIndex: null, count: 0 }); }}>
        <DialogContent className="bg-stone-800 border-amber-700 max-w-xs" data-testid="gm-undo-to-here-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-400 text-center text-sm flex items-center justify-center gap-2">
              <RotateCcw size={14} /> Rewind Plays?
            </DialogTitle>
            <DialogDescription className="text-stone-300 text-xs text-center pt-2">
              This will undo <span className="font-bold text-white">{undoToDialog.count} play{undoToDialog.count === 1 ? '' : 's'}</span> back to this point.
              <br /><span className="text-stone-500 text-[10px]">You can still Redo afterwards.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 mt-2">
            <Button
              className="flex-1 bg-stone-700 hover:bg-stone-600 text-white font-bold text-xs"
              onClick={() => setUndoToDialog({ open: false, targetIndex: null, count: 0 })}
              data-testid="gm-undo-to-here-cancel"
            >
              Cancel
            </Button>
            <Button
              className="flex-1 bg-amber-600 hover:bg-amber-500 text-white font-bold text-xs"
              onClick={() => {
                const idx = undoToDialog.targetIndex;
                setUndoToDialog({ open: false, targetIndex: null, count: 0 });
                if (idx !== null) handleUndoToIndex(idx);
              }}
              data-testid="gm-undo-to-here-confirm"
            >
              Rewind
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* CHANGE PITCHER DIALOG */}
      <Dialog open={changePitcherDialog} onOpenChange={(o) => { setChangePitcherDialog(o); if (!o) setNewPitcherName(''); }}>
        <DialogContent className="bg-stone-800 border-stone-700 max-w-xs">
          <DialogHeader><DialogTitle className="text-white text-center text-sm">Change Pitcher</DialogTitle></DialogHeader>
          {/* Add new pitcher by name */}
          <div className="flex gap-1.5 mt-2" data-testid="gm-add-new-pitcher-row">
            <Input
              placeholder="+ Type new pitcher name"
              value={newPitcherName}
              onChange={(e) => setNewPitcherName(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === 'Enter' && newPitcherName.trim() && !addingNewPitcher) {
                  e.preventDefault();
                  await handleAddNewPitcher();
                }
              }}
              className="h-8 text-xs bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
              data-testid="gm-new-pitcher-input"
            />
            <Button
              size="sm"
              className="h-8 bg-amber-600 hover:bg-amber-500 text-white font-bold text-xs px-3"
              disabled={!newPitcherName.trim() || addingNewPitcher}
              onClick={handleAddNewPitcher}
              data-testid="gm-new-pitcher-add-btn"
            >
              {addingNewPitcher ? '...' : 'Add'}
            </Button>
          </div>
          <div className="space-y-1.5 max-h-[350px] overflow-y-auto mt-2">
            {team?.players?.map(p => {
              const warn = getRestWarning(restMap, p.id);
              return (
                <button key={p.id}
                  className={cn("w-full flex items-center gap-2 p-2 rounded-lg text-xs text-left transition-all",
                    myPitcher?.id === p.id ? "bg-blue-900/60 border border-blue-600" : warn ? "bg-red-900/30 border border-red-700/50 hover:bg-red-900/40" : "bg-stone-700/50 hover:bg-stone-700"
                  )}
                  onClick={() => handlePitcherPick(p)}
                  data-testid={`pitcher-pick-${p.id}`}
                >
                  <span className="w-7 h-7 rounded bg-stone-600 flex items-center justify-center text-white font-bold text-[10px]">{p.jersey_number || '?'}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-semibold truncate">{p.name}</div>
                    {warn && <div className="text-red-300 text-[9px] leading-tight truncate" data-testid={`pitcher-rest-warn-${p.id}`}>⚠️ {warn}</div>}
                  </div>
                  {myPitcher?.id === p.id && <span className="text-[8px] bg-blue-600 text-white px-1 rounded ml-auto shrink-0">Current</span>}
                  {warn && myPitcher?.id !== p.id && <span className="text-[8px] bg-red-600 text-white px-1 rounded ml-auto shrink-0">REST</span>}
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* QUICK SUB — swap player up to bat or any slot on either team */}
      <Dialog open={quickSubOpen} onOpenChange={(o) => { setQuickSubOpen(o); if (!o) setQuickSubPick({ open: false, side: null, slot: null, currentName: '', newName: '' }); }}>
        <DialogContent className="bg-stone-800 border-amber-700 max-w-md" data-testid="gm-quick-sub-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-400 text-center text-sm flex items-center justify-center gap-2">
              <ArrowLeftRight size={16} /> Quick Sub
            </DialogTitle>
            <DialogDescription className="sr-only">Swap any player in the lineup — tap a slot to replace with a roster player or type a new name.</DialogDescription>
          </DialogHeader>

          {/* Team tabs */}
          <div className="grid grid-cols-2 gap-2 mt-2">
            <Button
              size="sm"
              className={cn("text-xs font-bold", quickSubTab === 'my' ? 'bg-green-700 hover:bg-green-600 text-white' : 'bg-stone-700 hover:bg-stone-600 text-stone-300')}
              onClick={() => setQuickSubTab('my')}
              data-testid="gm-quick-sub-my-tab"
            >
              {teamName}
            </Button>
            <Button
              size="sm"
              className={cn("text-xs font-bold", quickSubTab === 'opp' ? 'bg-red-700 hover:bg-red-600 text-white' : 'bg-stone-700 hover:bg-stone-600 text-stone-300')}
              onClick={() => setQuickSubTab('opp')}
              data-testid="gm-quick-sub-opp-tab"
            >
              {oppName}
            </Button>
          </div>

          <p className="text-stone-400 text-[10px] text-center mt-2">Tap any slot to swap that player. Tap the ⚾ to instantly sub for the current batter.</p>

          {/* Lineup list */}
          <div className="max-h-[50vh] overflow-y-auto space-y-1 mt-2">
            {quickSubTab === 'my' ? (
              gs.battingOrder.map((pid, i) => {
                const p = team?.players?.find(pl => pl.id === pid);
                const isCurrent = i === gs.currentBatterIndex && myTeamBatting;
                return (
                  <button
                    key={pid + '-' + i}
                    onClick={() => openQuickSubPick('my', i, p?.name || '')}
                    className={cn(
                      "w-full flex items-center gap-2 px-3 py-2 rounded-lg border text-left transition-colors",
                      isCurrent ? 'bg-amber-900/30 border-amber-600 text-amber-300' : 'bg-stone-900/60 border-stone-700 text-stone-200 hover:border-amber-600'
                    )}
                    data-testid={`gm-quick-sub-my-slot-${i}`}
                  >
                    <span className="w-5 text-[10px] font-bold text-stone-500">#{i + 1}</span>
                    {isCurrent && <span className="text-xs" title="Now batting">⚾</span>}
                    <span className="text-sm font-semibold flex-1 truncate">{p?.name || '—'}</span>
                    <span className="text-[10px] text-amber-400 uppercase tracking-wider">Swap</span>
                  </button>
                );
              })
            ) : (
              oppLineup.map((opp, i) => {
                const isCurrent = i === oppBatterIndex && !myTeamBatting;
                return (
                  <button
                    key={'opp-' + i}
                    onClick={() => openQuickSubPick('opp', i, opp?.name || '')}
                    className={cn(
                      "w-full flex items-center gap-2 px-3 py-2 rounded-lg border text-left transition-colors",
                      isCurrent ? 'bg-amber-900/30 border-amber-600 text-amber-300' : 'bg-stone-900/60 border-stone-700 text-stone-200 hover:border-amber-600'
                    )}
                    data-testid={`gm-quick-sub-opp-slot-${i}`}
                  >
                    <span className="w-5 text-[10px] font-bold text-stone-500">#{i + 1}</span>
                    {isCurrent && <span className="text-xs" title="Now batting">⚾</span>}
                    <span className="text-sm font-semibold flex-1 truncate">{opp?.name || '—'}</span>
                    <span className="text-[10px] text-amber-400 uppercase tracking-wider">Rename</span>
                  </button>
                );
              })
            )}
          </div>

          {/* Navigate to Defense (FieldPage) for position swaps */}
          {quickSubTab === 'my' && (
            <Button
              variant="outline"
              size="sm"
              className="w-full mt-3 bg-stone-900 border-stone-700 text-stone-300 hover:bg-stone-700 text-xs"
              onClick={() => { setQuickSubOpen(false); navigate('/field'); }}
              data-testid="gm-quick-sub-go-field"
            >
              Change Defensive Positions →
            </Button>
          )}
          <Button size="sm" variant="ghost" className="text-stone-400 hover:text-white" onClick={() => setQuickSubOpen(false)} data-testid="gm-quick-sub-close">
            Close
          </Button>
        </DialogContent>
      </Dialog>

      {/* QUICK SUB — secondary picker: pick existing player or type new */}
      <Dialog open={quickSubPick.open} onOpenChange={(o) => { if (!o) setQuickSubPick({ open: false, side: null, slot: null, currentName: '', newName: '' }); }}>
        <DialogContent className="bg-stone-800 border-amber-700 max-w-sm" data-testid="gm-quick-sub-pick-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-400 text-center text-sm">
              Sub for <span className="text-white">{quickSubPick.currentName || '#' + ((quickSubPick.slot ?? 0) + 1)}</span>
            </DialogTitle>
            <DialogDescription className="sr-only">Pick an existing player or type a new name.</DialogDescription>
          </DialogHeader>

          {/* Type new name */}
          <div className="flex gap-1.5 mt-2">
            <input
              type="text"
              placeholder={quickSubPick.side === 'my' ? 'Type new player name…' : 'Type opponent player name…'}
              value={quickSubPick.newName}
              onChange={(e) => setQuickSubPick({ ...quickSubPick, newName: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') quickSubAddNew(); }}
              className="flex-1 bg-stone-900 border border-stone-700 rounded-md px-2 py-1.5 text-white text-sm placeholder-stone-500"
              data-testid="gm-quick-sub-pick-new-input"
              autoFocus
            />
            <Button
              size="sm"
              className="bg-amber-600 hover:bg-amber-500 text-white text-xs font-bold px-3"
              disabled={!quickSubPick.newName.trim()}
              onClick={quickSubAddNew}
              data-testid="gm-quick-sub-pick-new-save"
            >
              Add + Sub In
            </Button>
          </div>

          {/* Existing roster (my team only — opponent is text-only) */}
          {quickSubPick.side === 'my' && (
            <>
              <p className="text-stone-500 text-[10px] uppercase tracking-wider mt-3 mb-1">Or pick from roster</p>
              <div className="max-h-56 overflow-y-auto space-y-1">
                {team?.players?.filter(p => p.id !== gs.battingOrder[quickSubPick.slot]).map(p => (
                  <button
                    key={p.id}
                    onClick={() => quickSubApplyExisting(p.id)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md bg-stone-900/60 border border-stone-700 hover:border-amber-500 text-left text-sm text-stone-200"
                    data-testid={`gm-quick-sub-pick-${p.id}`}
                  >
                    {p.jersey_number && <span className="text-[10px] text-stone-500 w-5">#{p.jersey_number}</span>}
                    <span className="truncate">{p.name}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* REST DAY WARNING DIALOG */}
      <Dialog open={restWarnDialog.open} onOpenChange={(o) => { if (!o) setRestWarnDialog({ open: false, player: null, warning: '' }); }}>
        <DialogContent className="bg-stone-800 border-red-700 max-w-xs" data-testid="rest-warn-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-400 text-center text-sm flex items-center justify-center gap-2">
              <AlertTriangle size={16} /> Rest Day Warning
            </DialogTitle>
            <DialogDescription className="text-stone-300 text-xs text-center pt-2">
              <span className="text-white font-bold">{restWarnDialog.player?.name}</span>
              <br />
              {restWarnDialog.warning}
              <br /><br />
              Using this pitcher before their required rest may violate pitch-count rules and risk arm injury.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 mt-2">
            <Button
              className="flex-1 bg-stone-700 hover:bg-stone-600 text-white font-bold text-xs"
              onClick={() => setRestWarnDialog({ open: false, player: null, warning: '' })}
              data-testid="rest-warn-cancel"
            >
              Cancel
            </Button>
            <Button
              className="flex-1 bg-red-700 hover:bg-red-600 text-white font-bold text-xs"
              onClick={() => {
                const p = restWarnDialog.player;
                setRestWarnDialog({ open: false, player: null, warning: '' });
                if (p) applyPitcherChange(p);
              }}
              data-testid="rest-warn-confirm"
            >
              Use Anyway
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* HIT DIALOG */}
      <Dialog open={hitDialog} onOpenChange={setHitDialog}>
        <DialogContent className="bg-stone-800 border-stone-700 max-w-sm" data-testid="hit-dialog">
          <DialogHeader><DialogTitle className="text-white text-center">HITS / REACH BASE</DialogTitle></DialogHeader>
          <div className="space-y-2 mt-2">
            <div className="grid grid-cols-2 gap-2">
              <Button className="h-14 bg-green-700 hover:bg-green-600 font-bold rounded-xl" onClick={() => handleHit('single')} data-testid="gm-single-btn">1B Single</Button>
              <Button className="h-14 bg-blue-700 hover:bg-blue-600 font-bold rounded-xl" onClick={() => handleHit('double')} data-testid="gm-double-btn">2B Double</Button>
              <Button className="h-14 bg-amber-600 hover:bg-amber-500 font-bold rounded-xl" onClick={() => handleHit('triple')} data-testid="gm-triple-btn">3B Triple</Button>
              <Button className="h-14 bg-red-600 hover:bg-red-500 font-bold rounded-xl" onClick={() => handleHit('homerun')} data-testid="gm-hr-btn">HR Home Run</Button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Button className="h-11 bg-stone-700 hover:bg-stone-600 text-xs font-bold rounded-xl" onClick={handleHBP} data-testid="gm-hbp-btn">HBP</Button>
              <Button className="h-11 bg-stone-700 hover:bg-stone-600 text-xs font-bold rounded-xl" onClick={() => handleSac(false)} data-testid="gm-sac-btn">SAC Out</Button>
              <Button className="h-11 bg-teal-700 hover:bg-teal-600 text-xs font-bold rounded-xl" onClick={() => handleSac(true)} data-testid="gm-sac-safe-btn">SAC Safe</Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button className="h-11 bg-purple-700 hover:bg-purple-600 text-xs font-bold rounded-xl" onClick={() => setErrorDialog(true)} data-testid="gm-error-btn">Error (E)</Button>
              <Button className="h-11 bg-stone-700 hover:bg-stone-600 text-xs font-bold rounded-xl" onClick={() => { setHitDialog(false); setFcDialog(true); }} data-testid="gm-fc-btn">FC Fielder's Choice</Button>
            </div>
            <Button className="w-full h-10 bg-indigo-800 hover:bg-indigo-700 text-xs font-bold rounded-xl" onClick={() => { setHitDialog(false); setRareDialog(true); }} data-testid="gm-rare-btn">
              RARE PLAYS
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ERROR DIALOG */}
      <Dialog open={errorDialog} onOpenChange={setErrorDialog}>
        <DialogContent className="bg-stone-800 border-stone-700 max-w-xs">
          <DialogHeader><DialogTitle className="text-white text-center">Error — How many bases?</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-2 mt-2">
            {[1,2,3].map(b => (
              <Button key={b} className="h-14 bg-purple-700 hover:bg-purple-600 font-bold rounded-xl" onClick={() => handleError(b)} data-testid={`gm-error-${b}-btn`}>{b}-Base</Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* HOME RUN TYPE DIALOG */}
      <Dialog open={hrTypeDialog} onOpenChange={setHrTypeDialog}>
        <DialogContent className="bg-stone-800 border-stone-700 max-w-sm" data-testid="hr-type-dialog">
          <DialogHeader>
            <DialogTitle className="text-white text-center">HOME RUN!</DialogTitle>
            <p className="text-stone-400 text-xs text-center mt-1">How did {batterName} score?</p>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-2 mt-3">
            <Button
              className="h-16 bg-amber-600 hover:bg-amber-500 font-bold rounded-xl flex-col gap-0.5"
              onClick={() => handleHomeRun('in_the_park')}
              data-testid="gm-hr-itp-btn"
            >
              <span className="text-base">In the Park</span>
              <span className="text-[10px] font-normal opacity-90">Ball stayed in play</span>
            </Button>
            <Button
              className="h-16 bg-red-600 hover:bg-red-500 font-bold rounded-xl flex-col gap-0.5"
              onClick={() => handleHomeRun('over_the_fence')}
              data-testid="gm-hr-otf-btn"
            >
              <span className="text-base">Over the Fence</span>
              <span className="text-[10px] font-normal opacity-90">Outta here!</span>
            </Button>
            <Button
              variant="ghost"
              className="text-stone-400 hover:text-white text-xs mt-1"
              onClick={() => { setHrTypeDialog(false); setHitDialog(true); }}
              data-testid="gm-hr-cancel-btn"
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* SOFTBALL RULES QUICK REFERENCE - softball games only */}
      <Dialog open={softballRulesDialog} onOpenChange={setSoftballRulesDialog}>
        <DialogContent className="bg-stone-800 border-amber-700 max-w-md" data-testid="softball-rules-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-300 flex items-center gap-2">
              <BookOpen size={18} /> Softball Quick Reference
            </DialogTitle>
            <p className="text-stone-400 text-xs">Common fastpitch rules that differ from baseball.</p>
          </DialogHeader>
          <div className="space-y-3 mt-2 max-h-[60vh] overflow-y-auto">
            {team?.dp_flex_enabled && (
              <div className="bg-amber-900/30 border border-amber-700 rounded-lg p-3" data-testid="rules-dp-flex">
                <div className="text-amber-300 font-bold text-sm flex items-center gap-1 mb-1">
                  <Check size={14} /> DP / FLEX (enabled)
                </div>
                <p className="text-stone-200 text-xs leading-relaxed">
                  <strong>DP</strong> bats but doesn&apos;t field. <strong>FLEX</strong> fields but doesn&apos;t bat.
                  Both can re-enter. The DP can also play defense (FLEX exits the game). Recognized as positions in the lineup card.
                </p>
              </div>
            )}
            <div className="bg-stone-700/40 border border-stone-600 rounded-lg p-3">
              <div className="text-amber-300 font-bold text-sm mb-1">Lookback Rule</div>
              <p className="text-stone-200 text-xs leading-relaxed">
                When the pitcher has the ball in the circle and a runner is off the base, the runner must
                immediately commit: advance to the next base or return. No bouncing back and forth.
                Violations = the runner is out (record as a normal Out in the OUT dialog).
              </p>
            </div>
            <div className="bg-stone-700/40 border border-stone-600 rounded-lg p-3">
              <div className="text-amber-300 font-bold text-sm mb-1">Courtesy Runner</div>
              <p className="text-stone-200 text-xs leading-relaxed">
                Pitcher and catcher may have a courtesy runner (any non-active player). Use
                &quot;Place Runner&quot; to add them to the bases without a re-pitch.
              </p>
            </div>
            <div className="bg-stone-700/40 border border-stone-600 rounded-lg p-3">
              <div className="text-amber-300 font-bold text-sm mb-1">No Leading Off</div>
              <p className="text-stone-200 text-xs leading-relaxed">
                Runners cannot leave the base until the pitcher releases the ball. Early departure = the runner
                is out (NFHS) or returned to the base (USSSA / some leagues).
              </p>
            </div>
            <div className="bg-stone-700/40 border border-stone-600 rounded-lg p-3">
              <div className="text-amber-300 font-bold text-sm mb-1">Dropped Third Strike</div>
              <p className="text-stone-200 text-xs leading-relaxed">
                In fastpitch, the batter is OUT on a dropped third strike (unlike baseball). Just record the
                strikeout as a normal Out — no need to advance the batter.
              </p>
            </div>
          </div>
          <Button
            className="mt-3 bg-amber-600 hover:bg-amber-500 text-white font-bold"
            onClick={() => setSoftballRulesDialog(false)}
            data-testid="softball-rules-close-btn"
          >
            Got it
          </Button>
        </DialogContent>
      </Dialog>

      {/* OUT DIALOG */}
      <Dialog open={outDialog} onOpenChange={setOutDialog}>
        <DialogContent className="bg-stone-800 border-stone-700 max-w-sm" data-testid="out-dialog">
          <DialogHeader><DialogTitle className="text-white text-center">RECORD AN OUT</DialogTitle></DialogHeader>
          <div className="space-y-2 mt-2">
            <Button className="w-full h-12 bg-red-700 hover:bg-red-600 font-bold rounded-xl" onClick={() => handleOut(null, `Easy Out - ${batterName}`)} data-testid="gm-easy-out-btn">Easy Out (Groundout/Flyout)</Button>
            <Button className="w-full h-12 bg-red-800 hover:bg-red-700 font-bold rounded-xl" onClick={() => handleOut(null, `Strikeout (K) - ${batterName}`)} data-testid="gm-k-btn">K Strikeout</Button>
            <Button className="w-full h-14 bg-stone-700 hover:bg-stone-600 font-bold text-xs rounded-xl flex-col gap-0.5" onClick={() => { setOutDialog(false); setDefenseMode(true); setDefenseSeq([]); }} data-testid="gm-defense-out-btn">
              <span className="flex items-center"><Target size={14} className="mr-2" /> Tap Defensive Positions</span>
              <span className="text-[10px] font-normal opacity-80">3-1 putout, 6-4-3 double play, F-7 flyout, etc.</span>
            </Button>
            {(gs.runners.first || gs.runners.second || gs.runners.third) && (
              <div className="space-y-2 pt-2 border-t border-stone-700">
                <p className="text-stone-400 text-xs font-semibold">Runner Outs:</p>
                {['first','second','third'].map(base => gs.runners[base] && (
                  <Button key={base} className="w-full h-10 bg-amber-800 hover:bg-amber-700 text-xs font-bold rounded-xl" onClick={() => handlePickOff(base)} data-testid={`gm-pickoff-${base}-btn`}>
                    Pick Off — {gs.runners[base]?.playerName || '?'} at {base}
                  </Button>
                ))}
              </div>
            )}
            <Button className="w-full h-10 bg-stone-700 hover:bg-stone-600 text-xs font-bold rounded-xl" onClick={() => { setOutDialog(false); setDropKDialog({ open: true, step: 'throw' }); }} data-testid="gm-dropk-btn">
              Dropped 3rd Strike
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* FC DIALOG */}
      <Dialog open={fcDialog} onOpenChange={setFcDialog}>
        <DialogContent className="bg-stone-800 border-stone-700 max-w-xs">
          <DialogHeader><DialogTitle className="text-white text-center">Fielder's Choice — Out at which base?</DialogTitle></DialogHeader>
          <div className="space-y-2 mt-2">
            {['first','second','third'].map(base => gs.runners[base] && (
              <Button key={base} className="w-full h-12 bg-stone-700 hover:bg-stone-600 font-bold rounded-xl" onClick={() => handleFC(base)}>
                Out at {base} ({gs.runners[base]?.playerName || '?'})
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* RARE PLAYS DIALOG */}
      <Dialog open={rareDialog} onOpenChange={setRareDialog}>
        <DialogContent className="bg-stone-800 border-stone-700 max-w-xs">
          <DialogHeader><DialogTitle className="text-white text-center">RARE PLAYS</DialogTitle></DialogHeader>
          <div className="space-y-2 mt-2">
            <Button className="w-full h-12 bg-indigo-700 hover:bg-indigo-600 font-bold rounded-xl" onClick={handleIntentionalWalk} data-testid="gm-ibb-btn">Intentional Walk (IBB)</Button>
            <Button className="w-full h-12 bg-indigo-700 hover:bg-indigo-600 font-bold rounded-xl" onClick={handleGroundRuleDouble} data-testid="gm-grd-btn">Ground Rule Double</Button>
            <Button className="w-full h-12 bg-indigo-700 hover:bg-indigo-600 font-bold rounded-xl" onClick={handleInfieldFly} data-testid="gm-ifly-btn">Infield Fly Rule</Button>
            <Button className="w-full h-12 bg-red-800 hover:bg-red-700 font-bold rounded-xl" onClick={handleTriplePlay} data-testid="gm-tp-btn">Triple Play</Button>
            <Button className="w-full h-12 bg-stone-700 hover:bg-stone-600 text-xs font-bold rounded-xl" onClick={() => handleInterference("Catcher's")} data-testid="gm-ci-btn">Catcher's Interference (bat hits glove)</Button>
            <Button className="w-full h-12 bg-stone-700 hover:bg-stone-600 text-xs font-bold rounded-xl" onClick={() => handleInterference("Batter's")} data-testid="gm-bi-btn">Batter's Interference</Button>
            <Button className="w-full h-12 bg-amber-700 hover:bg-amber-600 text-xs font-bold rounded-xl" onClick={() => { setRareDialog(false); setCatcherBalkDialog(true); }} data-testid="gm-catcher-balk-btn">Catcher Balk</Button>
            <Button className="w-full h-12 bg-stone-700 hover:bg-stone-600 text-xs font-bold rounded-xl" onClick={() => handleInterference("Obstruction")} data-testid="gm-obstruction-btn">Obstruction</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* CATCHER BALK SUB-DIALOG */}
      <Dialog open={catcherBalkDialog} onOpenChange={setCatcherBalkDialog}>
        <DialogContent className="bg-stone-800 border-stone-700 max-w-xs">
          <DialogHeader>
            <DialogTitle className="text-white text-center">CATCHER BALK</DialogTitle>
            <p className="text-stone-400 text-xs text-center mt-1">Pick the violation</p>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <Button
              className="w-full min-h-14 py-2 bg-amber-700 hover:bg-amber-600 text-left rounded-xl flex flex-col items-start justify-center px-3"
              onClick={handleCatcherBalkSqueeze}
              data-testid="gm-catcher-balk-squeeze-btn"
            >
              <span className="font-bold text-sm">Squeeze / Steal of Home</span>
              <span className="text-[10px] text-amber-100 leading-tight mt-0.5">Catcher blocked the plate during a steal — R3 scores · batter → 1B</span>
            </Button>
            <Button
              className="w-full min-h-14 py-2 bg-amber-700 hover:bg-amber-600 text-left rounded-xl flex flex-col items-start justify-center px-3"
              onClick={handleCatcherBalkDetached}
              data-testid="gm-catcher-balk-detached-btn"
            >
              <span className="font-bold text-sm">Detached Equipment</span>
              <span className="text-[10px] text-amber-100 leading-tight mt-0.5">Catcher touched a pitched ball with mask/cap — all runners +1 base</span>
            </Button>
            <Button
              className="w-full h-10 bg-stone-700 hover:bg-stone-600 text-xs font-bold rounded-xl"
              onClick={() => { setCatcherBalkDialog(false); setRareDialog(true); }}
              data-testid="gm-catcher-balk-back-btn"
            >
              ← Back to Rare Plays
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* RUNNER ADVANCE AFTER OUT DIALOG */}
      <Dialog open={runnerAdvanceDlg.open} onOpenChange={o => { if (!o) setRunnerAdvanceDlg({ open: false, state: null, desc: '' }); }}>
        <DialogContent className="bg-stone-800 border-stone-700 max-w-xs">
          <DialogHeader><DialogTitle className="text-white text-center text-sm">Runners Advance?</DialogTitle></DialogHeader>
          {(() => {
            const s = runnerAdvanceDlg.state;
            if (!s) return null;
            const bases = ['first', 'second', 'third'].filter(b => s.runners[b]);
            if (bases.length === 0) return <p className="text-stone-400 text-center text-xs">No runners</p>;
            return (
              <div className="space-y-3 mt-2">
                <Button className="w-full h-10 bg-stone-700 hover:bg-stone-600 font-bold text-xs rounded-xl" onClick={() => setRunnerAdvanceDlg({ open: false, state: null, desc: '' })} data-testid="gm-no-advance-btn">
                  No — Runners Stay
                </Button>
                <div className="border-t border-stone-700 pt-2">
                  <p className="text-stone-400 text-xs mb-2 text-center">Move runners:</p>
                  {bases.map(base => {
                    const runner = s.runners[base];
                    const name = runner?.playerName || '?';
                    const next = base === 'first' ? 'second' : base === 'second' ? 'third' : 'home';
                    return (
                      <Button key={base} className="w-full h-10 mb-1 bg-green-800 hover:bg-green-700 text-xs font-bold rounded-xl" onClick={() => {
                        const p = {}; p[base] = next;
                        handleRunnerAdvanceConfirm(p);
                      }} data-testid={`gm-advance-${base}-btn`}>
                        {name} → {next}
                      </Button>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* PINCH HITTER DIALOG */}
      <Dialog open={phDialog.open} onOpenChange={o => { if (!o) setPhDialog({ open: false, playerIndex: null, playerId: null, newName: '' }); }}>
        <DialogContent className="bg-stone-800 border-stone-700 max-w-xs">
          <DialogHeader><DialogTitle className="text-white text-center text-sm">Pinch Hitter</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-stone-400 text-xs text-center">
              Replacing #{phDialog.playerIndex != null ? phDialog.playerIndex + 1 : '?'} in lineup
            </p>
            <input type="text" value={phDialog.newName} onChange={e => setPhDialog(prev => ({ ...prev, newName: e.target.value }))}
              placeholder="New batter name" className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              data-testid="ph-name-input" autoFocus />
            <div className="flex gap-2">
              <Button className="flex-1 bg-stone-700" onClick={() => setPhDialog({ open: false, playerIndex: null, playerId: null, newName: '' })}>Cancel</Button>
              <Button className="flex-1 bg-amber-600 hover:bg-amber-500 font-bold" onClick={handlePhConfirm} disabled={!phDialog.newName.trim()} data-testid="ph-confirm-btn">Sub In</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* STEAL DIALOG */}
      <Dialog open={stealDialog.open} onOpenChange={o => !o && setStealDialog({ open: false, mode: 'steal', destinations: {}, caughtBase: null })}>
        <DialogContent className="bg-stone-800 border-stone-700 max-w-xs">
          <DialogHeader><DialogTitle className="text-white text-center">{stealDialog.mode === 'caught' ? 'Caught Stealing' : 'Stolen Base'}</DialogTitle></DialogHeader>
          {stealDialog.mode === 'caught' ? (
            <div className="space-y-2 mt-2">
              <p className="text-stone-400 text-xs">Which runner was caught?</p>
              {['first','second','third'].map(base => gs.runners[base] && (
                <Button key={base} className="w-full h-10 bg-red-800 hover:bg-red-700 text-xs font-bold rounded-xl" onClick={() => handleCaughtOut(base)} data-testid={`gm-caught-${base}-btn`}>
                  {gs.runners[base]?.playerName || '?'} at {base}
                </Button>
              ))}
            </div>
          ) : (
            <div className="space-y-3 mt-2">
              <p className="text-stone-300 text-xs">
                Tap each runner&apos;s destination. Skip a runner to leave them put.
              </p>
              {(() => {
                const validDestinations = {
                  first:  [{ key: '2nd',  label: '2B' }, { key: '3rd',  label: '3B' }, { key: 'home', label: 'HOME' }],
                  second: [{ key: '3rd',  label: '3B' }, { key: 'home', label: 'HOME' }],
                  third:  [{ key: 'home', label: 'HOME' }],
                };
                const baseLabels = { first: '1B', second: '2B', third: '3B' };
                const anyRunners = ['first','second','third'].some(b => gs.runners[b]);
                if (!anyRunners) {
                  return <p className="text-amber-300 text-xs italic text-center py-3">No runners on base. Tap close.</p>;
                }
                return ['first','second','third'].map(base => gs.runners[base] && (
                  <div key={base} className="bg-stone-700/70 rounded-xl p-2.5 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-white text-xs font-bold">
                        {gs.runners[base]?.playerName || '?'}{' '}
                        <span className="text-stone-400 font-normal">on {baseLabels[base]}</span>
                      </span>
                      {stealDialog.destinations[base] && (
                        <button
                          type="button"
                          onClick={() => setStealDialog(prev => {
                            const d = { ...prev.destinations };
                            delete d[base];
                            return { ...prev, destinations: d };
                          })}
                          className="text-[10px] text-stone-400 hover:text-stone-200 underline"
                          data-testid={`gm-steal-clear-${base}`}
                        >
                          clear
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-1.5">
                      {validDestinations[base].map(d => {
                        const active = stealDialog.destinations[base] === d.key;
                        return (
                          <button
                            type="button"
                            key={d.key}
                            onClick={() => setStealDialog(prev => ({
                              ...prev,
                              destinations: { ...prev.destinations, [base]: d.key }
                            }))}
                            className={cn(
                              'h-9 rounded-lg text-[11px] font-bold uppercase tracking-wider transition',
                              active
                                ? 'bg-amber-500 text-stone-900 ring-2 ring-amber-300'
                                : 'bg-stone-600 hover:bg-stone-500 text-stone-200'
                            )}
                            data-testid={`gm-steal-${base}-to-${d.key}`}
                          >
                            {d.label}
                          </button>
                        );
                      })}
                      {/* pad with empty cells so 1st-base row stays 3-wide visually */}
                      {Array.from({ length: 3 - validDestinations[base].length }).map((_, i) => (
                        <div key={`pad-${i}`} aria-hidden="true" />
                      ))}
                    </div>
                  </div>
                ));
              })()}
              <Button
                className="w-full bg-green-700 hover:bg-green-600 font-bold rounded-xl mt-2"
                onClick={handleStealConfirm}
                disabled={Object.keys(stealDialog.destinations || {}).length === 0}
                data-testid="gm-steal-confirm"
              >
                Confirm Steal
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* WILD PITCH / PASSED BALL / BALK */}
      <Dialog open={wildPitchDialog} onOpenChange={setWildPitchDialog}>
        <DialogContent className="bg-stone-800 border-stone-700 max-w-xs">
          <DialogHeader><DialogTitle className="text-white text-center">Advance Runners</DialogTitle></DialogHeader>
          <div className="space-y-2 mt-2">
            <Button className="w-full h-12 bg-stone-700 hover:bg-stone-600 font-bold rounded-xl" onClick={() => handleAdvanceAll('Wild Pitch')}>Wild Pitch (WP)</Button>
            <Button className="w-full h-12 bg-stone-700 hover:bg-stone-600 font-bold rounded-xl" onClick={() => handleAdvanceAll('Passed Ball')}>Passed Ball (PB)</Button>
            <Button className="w-full h-12 bg-stone-700 hover:bg-stone-600 font-bold rounded-xl" onClick={() => handleAdvanceAll('Balk')}>Balk (BK)</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* DROPPED K */}
      <Dialog open={dropKDialog.open} onOpenChange={o => !o && setDropKDialog({ open: false, step: 'throw' })}>
        <DialogContent className="bg-stone-800 border-stone-700 max-w-xs">
          <DialogHeader><DialogTitle className="text-white text-center">Dropped 3rd Strike</DialogTitle></DialogHeader>
          {dropKDialog.step === 'throw' ? (
            <div className="space-y-2 mt-2">
              <p className="text-stone-400 text-xs text-center">Did the throw to 1st get the out?</p>
              <Button className="w-full h-12 bg-green-700 hover:bg-green-600 font-bold rounded-xl" onClick={() => handleDropK(true)}>Yes — OUT at 1st</Button>
              <Button className="w-full h-12 bg-red-700 hover:bg-red-600 font-bold rounded-xl" onClick={() => setDropKDialog({ open: true, step: 'safe' })}>No — Runner Safe</Button>
            </div>
          ) : (
            <div className="space-y-2 mt-2">
              <p className="text-stone-400 text-xs text-center">Runner reached which base?</p>
              {['first','second','third'].map(b => (
                <Button key={b} className="w-full h-10 bg-stone-700 hover:bg-stone-600 text-xs font-bold rounded-xl" onClick={() => handleDropK(false, b)}>{b}</Button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* RUNNER PLACEMENT */}
      <Dialog open={runnerDialog.open} onOpenChange={o => !o && setRunnerDialog({ open: false, state: null, desc: '', placements: {}, batterBase: null, batterInfo: null, isSac: false, hitType: null })}>
        <DialogContent className="bg-stone-800 border-stone-700 max-w-sm">
          <DialogHeader><DialogTitle className="text-white text-center text-sm">Where did the runners end up?</DialogTitle><DialogDescription className="text-stone-400 text-center text-xs">{runnerDialog.desc}</DialogDescription></DialogHeader>
          <div className="space-y-3 mt-2">
            {['third','second','first'].map(base => {
              const runner = runnerDialog.state?.runners?.[base];
              if (!runner) return null;
              const dest = runnerDialog.placements[base] || base;
              return (
                <div key={base} className="bg-stone-700 rounded-lg p-2">
                  <p className="text-amber-300 text-xs font-semibold mb-1">{runner?.playerName || '?'} (was on {base})</p>
                  <div className="flex gap-1 flex-wrap">
                    {['first','second','third','home','out'].map(d => (
                      <button key={d} onClick={() => setRunnerDialog(prev => ({ ...prev, placements: { ...prev.placements, [base]: d } }))}
                        className={cn("px-2 py-1 rounded text-[10px] font-bold transition-all", dest === d ? (d === 'home' ? 'bg-green-600 text-white' : d === 'out' ? 'bg-red-600 text-white' : 'bg-amber-600 text-white') : 'bg-stone-600 text-stone-300')}>
                        {d === 'home' ? 'SCORES' : d === 'out' ? 'OUT' : d.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
            <Button className="w-full bg-green-700 hover:bg-green-600 font-bold rounded-xl" onClick={handleRunnerConfirm}><Check size={16} className="mr-2" /> Confirm</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* FLYOUT TAG-UP */}
      <Dialog open={flyoutDialog.open} onOpenChange={o => !o && setFlyoutDialog({ open: false, state: null, desc: '', runners: {} })}>
        <DialogContent className="bg-stone-800 border-stone-700 max-w-xs">
          <DialogHeader><DialogTitle className="text-white text-center text-sm">Tag-Up Advance?</DialogTitle><DialogDescription className="text-stone-400 text-xs text-center">Any runners tag up and advance?</DialogDescription></DialogHeader>
          <div className="space-y-2 mt-2">
            {['first','second','third'].map(base => flyoutDialog.state?.runners?.[base] && (
              <label key={base} className="flex items-center gap-2 p-2 bg-stone-700 rounded-lg cursor-pointer">
                <input type="checkbox" checked={!!flyoutDialog.runners[base]} onChange={e => setFlyoutDialog(prev => ({ ...prev, runners: { ...prev.runners, [base]: e.target.checked } }))} className="accent-green-500" />
                <span className="text-white text-xs">{flyoutDialog.state.runners[base]?.playerName || '?'} ({base}) → advance</span>
              </label>
            ))}
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 border-stone-600 text-stone-400" onClick={() => setFlyoutDialog({ open: false, state: null, desc: '', runners: {} })}>No Tag-Up</Button>
              <Button className="flex-1 bg-green-700 hover:bg-green-600" onClick={handleFlyoutConfirm}>Confirm</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* SPRAY CHART */}
      <Dialog open={sprayDialog.open} onOpenChange={o => !o && skipSpray()}>
        <DialogContent className="bg-stone-800 border-stone-700 max-w-sm">
          <DialogHeader><DialogTitle className="text-white text-center text-sm">Tap where {sprayDialog.batterName} hit it</DialogTitle></DialogHeader>
          <svg viewBox="0 0 100 100" className="w-full max-w-[280px] mx-auto aspect-square rounded-lg cursor-crosshair" style={{ background: '#166534' }} onClick={handleSprayTap} data-testid="spray-chart-svg">
            <path d="M 5 95 Q 5 5, 50 5 Q 95 5, 95 95" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="0.5" />
            <path d="M 50 85 L 75 60 L 50 35 L 25 60 Z" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="0.5" />
            <line x1="50" y1="85" x2="5" y2="15" stroke="rgba(255,255,255,0.15)" strokeWidth="0.3" />
            <line x1="50" y1="85" x2="95" y2="15" stroke="rgba(255,255,255,0.15)" strokeWidth="0.3" />
          </svg>
          <Button variant="outline" className="w-full border-stone-600 text-stone-400 text-xs" onClick={skipSpray} data-testid="skip-spray-btn">Skip</Button>
        </DialogContent>
      </Dialog>

      {/* ROSTER PROMPT — auto-shown when game has no batting order */}
      <Dialog open={rosterPromptDlg} onOpenChange={o => { if (!o) { sessionStorage.setItem(`gm_roster_prompt_${currentGameId}`, '1'); setRosterPromptDlg(false); } }}>
        <DialogContent className="bg-stone-800 border-emerald-700 max-w-xs" data-testid="gm-roster-prompt-dialog">
          <DialogHeader>
            <DialogTitle className="text-white text-center text-sm">No batting order set</DialogTitle>
            <DialogDescription className="text-stone-400 text-center text-xs">
              Load your full roster now so lineups and the AT BAT tag are ready — you can reorder or sub any time.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            <Button className="w-full h-11 bg-emerald-700 hover:bg-emerald-600 font-bold rounded-xl text-xs" onClick={() => { sessionStorage.setItem(`gm_roster_prompt_${currentGameId}`, '1'); setRosterPromptDlg(false); handleLoadRoster(); }} data-testid="gm-roster-prompt-load-btn">
              LOAD FULL ROSTER ({team?.players?.length || 0} players)
            </Button>
            <Button variant="outline" className="w-full h-10 border-stone-600 text-stone-300 text-xs rounded-xl" onClick={() => { sessionStorage.setItem(`gm_roster_prompt_${currentGameId}`, '1'); navigate('/lineup'); }} data-testid="gm-roster-prompt-lineup-btn">
              Set a custom order on the Lineup page
            </Button>
            <button className="w-full text-center text-[10px] text-stone-500 hover:text-stone-300 pt-1" onClick={() => { sessionStorage.setItem(`gm_roster_prompt_${currentGameId}`, '1'); setRosterPromptDlg(false); }} data-testid="gm-roster-prompt-dismiss-btn">
              Not now
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* BASE ACTION — tap an occupied base on the diamond */}
      <Dialog open={baseActionDlg.open} onOpenChange={o => !o && setBaseActionDlg({ open: false, base: null })}>
        <DialogContent className="bg-stone-800 border-emerald-700 max-w-xs" data-testid="gm-base-action-dialog">
          <DialogHeader>
            <DialogTitle className="text-white text-center text-sm">
              {gs.runners[baseActionDlg.base]?.playerName || 'Runner'} <span className="text-stone-400 font-normal">on {baseActionDlg.base}</span>
            </DialogTitle>
            <DialogDescription className="sr-only">Advance, score, remove or record an out on this runner.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            {(baseActionDlg.base === 'first' ? [['second', '→ 2nd Base'], ['third', '→ 3rd Base'], ['home', 'SCORES ⚡']] :
              baseActionDlg.base === 'second' ? [['third', '→ 3rd Base'], ['home', 'SCORES ⚡']] :
              [['home', 'SCORES ⚡']]).map(([dest, label]) => (
              <Button key={dest} className={cn("w-full h-11 font-bold rounded-xl text-xs", dest === 'home' ? 'bg-emerald-700 hover:bg-emerald-600' : 'bg-stone-700 hover:bg-stone-600')}
                onClick={() => advanceRunnerTo(baseActionDlg.base, dest)} data-testid={`gm-base-action-${dest}`}>
                {label}
              </Button>
            ))}
            <Button className="w-full h-11 bg-red-800 hover:bg-red-700 font-bold rounded-xl text-xs" onClick={() => { const b = baseActionDlg.base; setBaseActionDlg({ open: false, base: null }); handlePickOff(b); }} data-testid="gm-base-action-out">
              OUT — picked off / caught
            </Button>
            <Button variant="outline" className="w-full h-10 border-stone-600 text-stone-400 text-xs rounded-xl" onClick={() => removeRunnerFromBase(baseActionDlg.base)} data-testid="gm-base-action-remove">
              Remove runner (fix mistake — no out)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ADD RUNNER */}
      <Dialog open={addRunnerDlg.open} onOpenChange={o => !o && setAddRunnerDlg({ open: false, base: null, selected: null })}>
        <DialogContent className="bg-stone-800 border-stone-700 max-w-xs">
          <DialogHeader><DialogTitle className="text-white text-center text-sm">Place Runner on Base</DialogTitle></DialogHeader>
          <div className="space-y-2 mt-2">
            <p className="text-stone-400 text-xs">Select base:</p>
            <div className="flex gap-2">
              {['first','second','third'].map(b => (
                <Button key={b} size="sm" className={cn("flex-1 font-bold text-xs rounded-lg", addRunnerDlg.base === b ? 'bg-amber-600' : 'bg-stone-700')} onClick={() => setAddRunnerDlg(prev => ({ ...prev, base: b }))}>{b}</Button>
              ))}
            </div>
            <p className="text-stone-400 text-xs mt-2">Select player:</p>
            <div className="max-h-32 overflow-y-auto space-y-1">
              {team?.players?.map(p => (
                <button key={p.id} onClick={() => setAddRunnerDlg(prev => ({ ...prev, selected: p }))}
                  className={cn("w-full text-left p-1.5 rounded text-xs", addRunnerDlg.selected?.id === p.id ? 'bg-green-700 text-white' : 'bg-stone-700 text-stone-300')}>
                  {p.name}
                </button>
              ))}
            </div>
            <Button className="w-full bg-green-700 hover:bg-green-600 font-bold rounded-xl" onClick={handleAddRunner} disabled={!addRunnerDlg.base || !addRunnerDlg.selected}>Place Runner</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* SUBSTITUTION */}
      <Dialog open={subDialog.open} onOpenChange={o => !o && setSubDialog({ open: false, type: '', playerIndex: null, playerId: null, selected: null })}>
        <DialogContent className="bg-stone-800 border-stone-700 max-w-xs">
          <DialogHeader><DialogTitle className="text-white text-center text-sm">{subDialog.type === 'pinch' ? 'Pinch Hitter' : 'Substitution'}</DialogTitle></DialogHeader>
          <div className="space-y-2 mt-2">
            <p className="text-stone-400 text-xs">Replace {team?.players?.find(p => p.id === subDialog.playerId)?.name} with:</p>
            <div className="max-h-40 overflow-y-auto space-y-1">
              {benchPlayers.map(p => (
                <button key={p.id} onClick={() => setSubDialog(prev => ({ ...prev, selected: p.id }))}
                  className={cn("w-full text-left p-2 rounded text-xs", subDialog.selected === p.id ? 'bg-green-700 text-white' : 'bg-stone-700 text-stone-300')}>
                  {p.name} #{p.jersey_number}
                </button>
              ))}
            </div>
            <Button className="w-full bg-green-700 hover:bg-green-600 font-bold rounded-xl" onClick={handleSubConfirm} disabled={!subDialog.selected}>Confirm Sub</Button>
          </div>
        </DialogContent>
      </Dialog>

      <AbbreviationKey />
    </div>
  );
}
